import type { ServicePageConfig } from "../types";
import {
  getPackagePricingRows,
  PACKAGE_PRICE_HINT_SV,
  PACKAGE_PRICE_HINT_EN,
} from "../package-pricing";

export const flyttPages: ServicePageConfig[] = [
  {
    // Flytthjälp — config för /flytthjalp
    // Vinkel: Komplett flytthjälp med skåpbil OCH personal — helhetsservice från dörr till dörr.
    // Unik vinkel vs bärhjälp-flytt: vi tillhandahåller fordon + arbetare. Kunden behöver inte ordna egen bil.
    slug: "flytthjalp",
    serviceCategory: "flytt",

    meta: {
      title: "Flytthjälp – Smidig & Prisvärd Flytt med Skåpbil | Ungdomskraft",
      description:
        "Boka flytthjälp med skåpbil och personal. Vi packar, bär, kör och lossar – komplett flytt från dörr till dörr. Snabbt, prisvärt och med unga som gör skillnad. Boka idag!",
      h1: "Flytthjälp",
    },

    hero: {
      heading: "Professionell flytthjälp",
      intro:
        "Behöver du hjälp att flytta? Vi fixar hela flytten – med skåpbil och ett team av starka, engagerade ungdomar. Du behöver inte jaga hyrbil, ringa kompisar eller bära själv. Vi packar, bär ut, kör till nya adressen och bär in allt på rätt plats. Från etta till villa, från studentflytt till familjeflytt – vi tar hand om allt det tunga så att du kan fokusera på det nya hemmet.",
      heroImage: "/hero-flytthjalp.png",
    },

    featureCards: {
      sinceText: "Flytthjälp sedan 2014",
      storyText:
        "Sedan 2014 har Ungdomskraft hjälpt tusentals personer att flytta smidigt och prisvärt. Våra ungdomar är tränade i packning, lyftteknik och säker lastning – och de kör vår egen skåpbil hela vägen.",
      bookYourText: "Boka din flytthjälp",
      bookDescText:
        "Ange gamla och nya adressen, beskriv vad som ska flyttas och välj en tid. Du ser priset direkt – allt ingår.",
      vehicleTitleText: "Skåpbil och personal i ett paket",
      vehicleDescText:
        "Du behöver inte hyra bil eller organisera transport – vi kommer med skåpbil och team. Vi lastar, kör och lossar. Stort, litet, tungt eller skrymmande – vi har rätt fordon och tillräckligt med armar.",
    },

    itemsSection: {
      heading: "Vanliga saker vi flyttar",
      items: [
        {
          name: "Soffor & fåtöljer",
          description: "Hörnsoffor, bäddsoffor och tunga fåtöljer – vi skyddar och bär",
          image: "/sofa.webp",
        },
        {
          name: "Sängar & madrasser",
          description: "Dubbelsängar, continental-sängar och tunga madrasser",
          image: "/bed.webp",
        },
        {
          name: "Bord & stolar",
          description: "Matbord, skrivbord, stolar och matgrupper",
          image: "/table.webp",
        },
        {
          name: "Vitvaror",
          description: "Tvättmaskin, torktumlare, kylskåp och diskmaskin",
          image: "/washer.webp",
        },
        {
          name: "Garderober & byråer",
          description: "PAX-garderober, byråer, bokhyllor och hyllsystem",
          image: "/wardrobe.webp",
        },
        {
          name: "Flyttkartonger",
          description: "Fulla kartonger med böcker, kläder, kök och prylar",
          image: "/box.webp",
        },
      ],
    },

    seoContent: {
      mainHeading: "Komplett flytthjälp – skåpbil, personal och allt däremellan",
      mainText:
        "Att flytta ska inte behöva vara stressigt. Med Ungdomskrafts flytthjälp får du ett team av starka ungdomar och en skåpbil som kör hela sträckan – från gamla bostaden till den nya. Vi packar det som inte redan är packat, demonterar möbler som behöver tas isär, bär ut allt genom trappor och trånga dörrar, lastar skåpbilen så att allt sitter säkert, kör till nya adressen och lossar. Sedan bär vi in allt, ställer möblerna där du vill ha dem och monterar ihop det som behöver skruvas. Du slipper hyra bil, köra själv eller ringa runt efter vänner som kanske kan hjälpa. Allt ingår i en bokning: fordon, personal, skyddsmaterial och försäkring. Oavsett om du flyttar en etta i city eller en femrummare i villaområdet anpassar vi teamets storlek och fordonstyp efter ditt behov. Många av våra kunder bokar oss för att det är enklare och ofta billigare än att kombinera hyrbil, bensin och förlorad arbetstid. Du fokuserar på att göra i ordning det nya hemmet – vi tar hand om allt det tunga och logistiken runt flytten.",
      preparationHeading: "Förbered inför din flytt",
      preparationText:
        "Packa så mycket som möjligt i kartonger innan vi kommer – det sparar tid och gör flytten smidigare. Märk kartongerna med rum (kök, sovrum, badrum) så kan vi ställa allt på rätt plats direkt i nya bostaden. Demontera möbler som är för stora att bära hela: IKEA-garderober, sängramar och stora matbord går snabbare att flytta i delar. Töm kylskåp och frys minst ett dygn innan flytten och stäng av tvättmaskinen. Se till att det finns fri passage i trapphus och att hissen är bokad. Ha nycklar till både gamla och nya adressen redo och informera grannar om att det kan bli lite spring i trapphuset.",
      additionalSections: [
        {
          heading: "Studentflytt och liten lägenhet",
          content:
            "Flyttar du från en etta eller tvåa? Då behöver du inte boka en stor flyttfirma med tre lastbilar. Vår studentflytt-lösning är anpassad för mindre bohag: ett team på två personer och en skåpbil som rymmer allt du har. Perfekt vid lägenhetsbyte, när kontraktet löper ut eller vid flytt till ett nytt studentrum. Vi packar, bär och kör – du behöver inte göra något annat än att öppna dörren. Priset är fast och du ser det redan vid bokning, så det finns inga överraskningar. Har du redan egen bil eller släpvagn? Då kan du spara ännu mer med vår bärhjälp flytt – vi bidrar bara med musklerna.",

        },
        {
          heading: "Familjeflytt och större bohag",
          content:
            "Vid en större flytt – trea, fyra eller villa – skalar vi upp teamet. Fler bärare, en större skåpbil och om det behövs gör vi flera vändor. Vi planerar rutten och tidplanen så att flytten går smidigt utan onödiga väntetider. Tunga vitvaror kopplas loss och ansluts på nya adressen om du vill. Vi tar hand om allt från kassaskåp och tunga vitvaror till barnens cyklar och trädgårdsmöbler. Hela familjen kan åka till nya bostaden och börja ställa i ordning medan vi kör de sista lassen.",
        },
        {
          heading: "Vad ingår i flytthjälp?",
          listItems: [
            {
              label: "Skåpbil",
              text: "Vi kommer med rätt storlek på fordon – du behöver inte hyra eller köra själv.",
            },
            {
              label: "Packning",
              text: "Vi packar det som inte redan är i kartonger: kök, kläder, böcker, småprylar.",
            },
            {
              label: "Demontering",
              text: "Sängramar, garderober och bord som behöver tas isär innan de bärs ut.",
            },
            {
              label: "Bärning & lastning",
              text: "Vi bär ut allt genom trappor och trånga dörrar och lastar skåpbilen smart och säkert.",
            },
            {
              label: "Transport",
              text: "Vi kör hela sträckan – du behöver inte följa med i bilen om du inte vill.",
            },
            {
              label: "Lossning & montering",
              text: "Vi lossar, bär in, ställer möbler på plats och monterar det som behöver skruvas ihop.",
            },
          ],
          afterContent:
            "Du väljer vilka moment du behöver. Vill du bara ha hjälp med transport och bärning? Perfekt – boka bara det.",
        },
        {
          heading: "Varför boka flytthjälp med Ungdomskraft?",
          content:
            "Ungdomskraft är inte en vanlig flyttfirma. Vi är ett socialt företag som sysselsätter ungdomar och ger dem riktig arbetslivserfarenhet – och det märks i allt vi gör. När du bokar flytthjälp hos oss får du ett team av motiverade, energiska unga människor som verkligen vill leverera ett bra jobb. Varje flytt är för dem en chans att visa vad de går för, och den inställningen genomsyrar hela upplevelsen.\n\nSedan starten 2014 har vi genomfört tusentals flyttar i Stockholms- och Göteborgsområdet. Under den resan har vi gett tusentals ungdomar deras första riktiga arbetslivserfarenhet – med allt vad det innebär: att komma i tid, kommunicera professionellt med kunder, lösa problem på plats, samarbeta i team och ta ansvar för resultatet. Våra medarbetare lär sig packningsteknik, lyftteknik, säker lastning och hur man hanterar allt från ömtåligt porslin till 200-kiloskassaskåp. De tränas i att skydda kundens egendom, golv, väggar och dörrkarmar – och i att alltid lämna både gamla och nya bostaden i gott skick.\n\nDet här är inte bara bra för ungdomarna – det är bra för dig som kund. Du märker skillnaden i engagemang och energi. Våra team springer där andra går, dubbelkollar att allt är rätt packat och frågar alltid om det finns något mer de kan hjälpa till med. Vi har byggt en kultur där varje flytt ses som en möjlighet att överträffa förväntningarna, och det visar sig i de hundratals femstjärniga recensioner vi fått på Trustpilot och Google.\n\nMen det handlar inte bara om kvalitet – det handlar om mening. Varje flyttjobb du bokar genom Ungdomskraft bidrar direkt till att en ung person får arbetslivserfarenhet, lär sig ett yrke och bygger sitt cv. Många av våra medarbetare går vidare till fasta anställningar inom logistik, transport eller serviceyrken – och de bär med sig de värderingar och den arbetsmoral de lärt sig hos oss. Att välja oss innebär att din flytt blir en del av något större.\n\nVi tar också hållbarhet på allvar. Möbler och föremål som inte ska med till nya bostaden sorterar vi och kör till rätt återvinningsstation eller skänker till välgörenhetsorganisationer som Myrorna, Erikshjälpen och Stadsmissionen. Ingenting dumpas i onödan. Företagskunder som rapporterar CSR kan räkna in samarbetet med oss som ett konkret bidrag till ungdomssysselsättning och cirkulär ekonomi.\n\nDet är professionell flytthjälp med mening.\n\nFlytt av privatbostad ger rätt till RUT-avdrag med 50 % av arbetskostnaden – upp till 75 000 kr per person och år. Avdraget hanteras direkt på fakturan, så du betalar bara halva priset för arbetet. Det gör professionell flytthjälp till ett ännu smartare val.",
        },
      ],
    },

    faq: [
      {
        question: "Vad ingår i flytthjälpen?",
        answer:
          "Allt du behöver: skåpbil, personal, packning av det som inte redan är packat, bärning, lastning, transport till nya adressen, lossning, inbärning och montering av möbler. Du väljer vilka moment du behöver – vill du bara ha transport och bärning fungerar det också.",
      },
      {
        question: "Hur snabbt kan ni komma?",
        answer:
          "Vi erbjuder ofta flytthjälp med kort varsel – ibland redan samma dag eller nästa dag. Boka online så ser du tillgängliga tider direkt.",
      },
      {
        question: "Hur mycket kostar flytthjälp?",
        answer:
          "Priset beror på mängden bohag, avstånd och antal bärare. En studentflytt från en etta landar ofta på 995–2 495 kr. En större familjeflytt från en trea kan landa på 2 495–4 995 kr. Du ser priset direkt vid bokning.",
      },
      {
        question: "Kan jag få RUT-avdrag på flytthjälp?",
        answer:
          "Ja, flytthjälp ger rätt till RUT-avdrag. Du får avdrag på 50 % av arbetskostnaden, upp till 75 000 kr per person och år. Avdraget gäller flytt av din privatbostad – packning, bärning, lastning och montering. Vi hanterar RUT-avdraget direkt på fakturan, så du betalar bara halva arbetskostnaden.",
      },
      {
        question: "Behöver jag hyra bil själv?",
        answer:
          "Nej, vi kommer med vår egen skåpbil – det ingår i tjänsten. Du behöver inte hyra, köra eller tanka. Vill du ändå använda egen bil kan du boka bärhjälp flytt istället, där vi bara tillhandahåller extra armar och ben.",
      },
      {
        question: "Kan ni flytta vitvaror som tvättmaskin och kylskåp?",
        answer:
          "Ja, vi flyttar alla typer av vitvaror. Vi kopplar loss och säkrar dem för transport, och kan ansluta dem igen på den nya adressen om du vill. Tänk på att tömma kylskåp och frys minst ett dygn innan flytten.",
      },
      {
        question: "Hur många bärare kommer?",
        answer:
          "Vi anpassar teamet efter din flytt. En etta eller tvåa behöver oftast två bärare. En trea eller fyra med mycket möbler kan kräva tre till fyra personer. Ange vad som ska flyttas vid bokning så rekommenderar vi rätt antal.",
      },
      {
        question: "Skyddar ni mina möbler under flytten?",
        answer:
          "Ja, vi har möbelfiltar, bärremmar, skyddsmattor och spännband med oss. Vi skyddar golv, väggar och dörrkarmar vid utbärning och inbärning. Allt är försäkrat – om något skulle skadas ersätter vi det.",
      },
      {
        question: "Kan ni hjälpa till med flytt på helger och kvällar?",
        answer:
          "Ja, vi erbjuder flytthjälp även på helger och vissa kvällar. Många av våra kunder föredrar att flytta på en lördag för att slippa ta ledigt från jobbet. Boka online så ser du alla tillgängliga tider – inklusive helger.",
      },
      {
        question: "Erbjuder ni flytthjälp för företag och kontor?",
        answer:
          "Absolut. Vi utför kontorsflytt och företagsflytt av alla storlekar – från enskilda arbetsplatser till hela kontor. Vi hanterar skrivbord, kontorsstolar, arkivskåp, IT-utrustning och allt annat som behöver flyttas. Kontakta oss för en offert anpassad efter ert behov.",
      },
      {
        question: "Kan jag få flytthjälp redan samma vecka?",
        answer:
          "Ja, vi har ofta lediga tider med kort varsel. Många kunder bokar flytt redan för samma vecka, ibland redan inom ett par dagar. Boka online så ser du direkt vilka tider som är tillgängliga.",
      },
      {
        question: "Hur förbereder jag ömtåliga saker inför flytten?",
        answer:
          "Linda ömtåligt porslin, glas och elektronik i bubbelplast eller tidningspapper och lägg dem i kartonger med stötdämpande material. Märk kartongerna med \"ömtåligt\" så att vårt team vet att hantera dem extra varsamt. Har du inte hunnit packa ömtåliga saker kan vi göra det åt dig – vi har skyddsmaterial med oss.",
      },
      {
        question: "Kan ni mellanlagra saker under flytten?",
        answer:
          "Vi erbjuder inte egen lagerförvaring, men vi kan hjälpa dig att transportera saker till ett förråd eller magasin om du har ett. Vid flytt med olika inflytt- och avflyttdatum kan vi planera transporten i etapper så att allt hamnar på rätt plats vid rätt tidpunkt.",
      },
      {
        question: "Erbjuder ni flytthjälp för långdistans eller mellan städer?",
        answer:
          "Ja, vi utför flyttar både inom och mellan Stockholm och Göteborg. Har du en längre flytt beräknas priset utifrån avstånd och mängd bohag. Ange gamla och nya adressen vid bokning så ser du kostnaden direkt – oavsett om det gäller flytt inom staden eller mellan regioner.",
      },
      {
        question: "I vilka kommuner erbjuder ni flytthjälp?",
        answer:
          "Vi erbjuder flytthjälp i hela Stockholmsområdet: Botkyrka, Danderyd, Ekerö, Haninge, Huddinge, Järfälla, Lidingö, Nacka, Norrtälje, Nykvarn, Nynäshamn, Salem, Sigtuna, Sollentuna, Solna, Stockholm, Sundbyberg, Södertälje, Tyresö, Täby, Upplands Väsby, Upplands-Bro, Vallentuna, Vaxholm, Värmdö och Österåker. Vi finns även i Göteborgsområdet: Ale, Alingsås, Göteborg, Härryda, Kungälv, Kungsbacka, Lerum, Lilla Edet, Mölndal, Partille, Stenungsund, Tjörn och Öckerö.",
      },
    ],

    priceHint: PACKAGE_PRICE_HINT_SV.transport,

    pricingRows: getPackagePricingRows("transport", "sv"),

    dynamicHeadings: {
      threeSteps: "Flytthjälp i 3 enkla steg",
      step2Text:
        "Våra ungdomar packar, bär ut allt, lastar vår skåpbil, kör till nya adressen och bär in allt på rätt plats. Du behöver inte lyfta ett finger – vi sköter hela flytten.",
      step3Title: "Allt på plats",
      step3Desc:
        "Vi lossar och bär in allt i ditt nya hem. Möbler ställs på rätt plats och det som demonterats monteras ihop igen. Du kan börja leva i nya hemmet direkt.",
      ctaHeading: "Boka flytthjälp idag",
      ctaText:
        "Ange gamla och nya adressen, beskriv ditt bohag och välj en tid som passar. Du ser priset direkt – skåpbil och personal ingår.",
    },

    en: {
      slug: "moving-help",
      meta: {
        title: "Moving Help – Affordable Full-Service Moves | Ungdomskraft",
        description:
          "Book moving help in Stockholm & Gothenburg – van and crew included. We pack, carry, drive and unload door to door. Fixed price. Book today!",
        h1: "Moving help",
      },
      hero: {
        heading: "Professional moving help",
        intro:
          "Need help moving? We handle the entire move — with a van and a team of strong, committed young workers. No need to rent a vehicle, call friends or carry anything yourself. We pack, carry out, drive to the new address and carry everything in. From studios to houses, student moves to family moves — we take care of the heavy work so you can focus on settling in.",
      },
      featureCards: {
        sinceText: "Moving help since 2014",
        bookYourText: "Book your move",
        bookDescText:
          "Enter the old and new address, describe what needs moving and pick a time. You see the price instantly — everything is included.",
        vehicleTitleText: "Van and crew in one package",
        vehicleDescText:
          "No need to rent a vehicle or organise transport — we arrive with a van and a full team. We load, drive and unload. Big, small, heavy or bulky — we have the right vehicle and enough arms.",
      },
      itemsSection: {
        heading: "Common items we move",
        items: [
          {
            name: "Sofas & armchairs",
            description: "Corner sofas, sofa beds and heavy armchairs — protected and carried",
            image: "/sofa.webp",
          },
          {
            name: "Beds & mattresses",
            description: "Double beds, continental beds and heavy mattresses",
            image: "/bed.webp",
          },
          {
            name: "Tables & chairs",
            description: "Dining tables, desks, chairs and dining sets",
            image: "/table.webp",
          },
          {
            name: "Appliances",
            description: "Washing machines, tumble dryers, fridges and dishwashers",
            image: "/washer.webp",
          },
          {
            name: "Wardrobes & dressers",
            description: "PAX wardrobes, dressers, bookcases and shelving units",
            image: "/wardrobe.webp",
          },
          {
            name: "Moving boxes",
            description: "Packed boxes with books, clothes, kitchen items and belongings",
            image: "/box.webp",
          },
        ],
      },
      seoContent: {
        mainHeading: "Complete moving help — van, crew and everything in between",
        mainText:
          "Moving shouldn't be stressful. With Ungdomskraft's moving help you get a team of strong young workers and a van that drives the whole distance — from the old home to the new one. We pack what isn't already packed, disassemble furniture that needs taking apart, carry everything out through stairwells and narrow doorways, load the van so everything sits securely, drive to the new address and unload. Then we carry everything in, place furniture where you want it and assemble anything that needs putting together. You don't need to rent a vehicle, drive yourself or ring around for friends who might be able to help. Everything is included in one booking: vehicle, crew, protective materials and insurance. Whether you're moving a studio in the city or a five-room house in the suburbs, we adapt the team size and vehicle type to your needs. Many of our customers book us because it's simpler — and often cheaper — than combining a rental van, petrol and lost working hours. You focus on getting the new home in order, we handle the heavy lifting and the logistics.",
        preparationHeading: "Preparing for your move",
        preparationText:
          "Pack as much as possible into boxes before we arrive — it saves time and makes the move smoother. Label boxes by room (kitchen, bedroom, bathroom) so we can put everything in the right place straight away. Disassemble furniture that is too large to carry whole: IKEA wardrobes, bed frames and large dining tables are much easier to move in pieces. Empty the fridge and freezer at least a day before the move and disconnect the washing machine. Make sure stairwells are clear and the lift is booked if there is one. Have keys to both the old and new home ready and let neighbours know there may be some traffic in the stairwell.",
        additionalSections: [
          {
            heading: "Student moves and small flats",
            content:
              "Moving from a studio or one-bed? You don't need a big removal company with three lorries. Our student-move solution is built for smaller households: a team of two and a van that fits everything you own. Perfect when swapping flats, when the lease is up or when moving to a new student room. We pack, carry and drive — all you need to do is open the door. The price is fixed and you see it when booking, so there are no surprises. Already have your own car or trailer? You can save even more with our carrying help for moving — we just bring the muscle.",

          },
          {
            heading: "Family moves and larger homes",
            content:
              "For a bigger move — two-bed, three-bed or house — we scale the team up. More carriers, a larger van and, if needed, multiple trips. We plan the route and schedule so the move runs smoothly without unnecessary waiting. Heavy appliances are disconnected and reconnected at the new address if you wish. We handle everything from safes and heavy appliances to children's bikes and garden furniture. The whole family can head to the new home and start setting up while we drive the last loads.",
          },
          {
            heading: "What's included in moving help?",
            listItems: [
              {
                label: "Van",
                text: "We arrive with the right size vehicle — no need to rent or drive yourself.",
              },
              {
                label: "Packing",
                text: "We pack what isn't already in boxes: kitchen, clothes, books, small items.",
              },
              {
                label: "Disassembly",
                text: "Bed frames, wardrobes and tables that need taking apart before carrying out.",
              },
              {
                label: "Carrying & loading",
                text: "We carry everything out through stairs and narrow doorways and load the van smartly and securely.",
              },
              {
                label: "Transport",
                text: "We drive the full distance — you don't need to ride along if you'd rather go ahead.",
              },
              {
                label: "Unloading & assembly",
                text: "We unload, carry in, place furniture and assemble anything that needs putting together.",
              },
            ],
            afterContent:
              "You choose which parts you need. Just want transport and carrying? No problem — book exactly that.",
          },
          {
            heading: "Why book moving help with Ungdomskraft?",
            content:
              "Ungdomskraft is not an ordinary removal company. We are a social enterprise that employs young people and gives them real work experience — and it shows in everything we do. When you book moving help with us, you get a team of motivated, energetic young workers who genuinely want to deliver a great job. Every move is a chance for them to prove themselves, and that mindset runs through the entire experience.\n\nSince our founding in 2014, we have carried out thousands of moves across the Stockholm and Gothenburg regions. Along the way, we have given thousands of young people their first real work experience — with everything that entails: turning up on time, communicating professionally with customers, solving problems on-site, collaborating as a team and taking responsibility for the result. Our workers learn packing techniques, lifting techniques, safe loading and how to handle everything from delicate china to 200-kilo safes. They are trained to protect the customer's belongings, floors, walls and door frames — and to always leave both the old and new home in good condition.\n\nThis is not just good for the young people — it is good for you as a customer. You notice the difference in commitment and energy. Our teams run where others walk, double-check that everything is properly packed and always ask if there is anything else they can help with. We have built a culture where every move is seen as an opportunity to exceed expectations, and it shows in the hundreds of five-star reviews we have received on Trustpilot and Google.\n\nBut it is not just about quality — it is about meaning. Every move you book through Ungdomskraft directly contributes to a young person gaining work experience, learning a trade and building their CV. Many of our workers go on to permanent positions in logistics, transport or service industries — carrying with them the values and work ethic they learned with us. Choosing us means your move becomes part of something bigger.\n\nWe also take sustainability seriously. Furniture and items that are not coming to the new home are sorted and taken to the right recycling station or donated to charities such as Myrorna, Erikshjälpen and Stadsmissionen. Nothing is dumped unnecessarily. Corporate customers reporting CSR can count their collaboration with us as a tangible contribution to youth employment and the circular economy.\n\nIt is professional moving help with purpose.\n\nMoving your private residence qualifies for the Swedish RUT tax deduction – 50% of the labour cost, up to SEK 75,000 per person per year. The deduction is handled directly on the invoice, so you only pay half the price for the work. That makes professional moving help an even smarter choice.",
          },
        ],
      },
      faq: [
        {
          question: "What's included in the moving help?",
          answer:
            "Everything you need: a van, crew, packing of anything not already packed, carrying, loading, transport to the new address, unloading, carrying in and furniture assembly. You choose which parts you need — if you only want transport and carrying, that works too.",
        },
        {
          question: "How quickly can you come?",
          answer:
            "We often offer moving help at short notice — sometimes the same day or the next day. Book online and you'll see available times straight away.",
        },
        {
          question: "How much does moving help cost?",
          answer:
            "The price depends on the volume of belongings, distance and number of carriers. A student move from a studio often comes to SEK 995–2,495. A larger family move from a two- or three-bed can come to SEK 2,495–4,995. You see the price instantly when booking.",
        },
        {
          question: "Can I get a RUT tax deduction on moving help?",
          answer:
            "Yes, moving help qualifies for the Swedish RUT tax deduction. You get a 50% deduction on the labour cost, up to SEK 75,000 per person per year. The deduction covers moving your private residence – packing, carrying, loading and assembly. We handle the RUT deduction directly on the invoice, so you only pay half the labour cost.",
        },
        {
          question: "Do I need to rent a vehicle myself?",
          answer:
            "No, we arrive with our own van — it's included in the service. You don't need to rent, drive or refuel. If you'd rather use your own car, you can book carrying help for moving instead, where we just provide extra arms and legs.",
        },
        {
          question: "Can you move appliances like washing machines and fridges?",
          answer:
            "Yes, we move all types of appliances. We disconnect and secure them for transport, and can reconnect them at the new address if you wish. Remember to empty the fridge and freezer at least a day before the move.",
        },
        {
          question: "How many carriers will come?",
          answer:
            "We adapt the team to your move. A studio or one-bed usually needs two carriers. A two- or three-bed with lots of furniture may need three to four. Describe what needs moving when booking and we'll recommend the right number.",
        },
        {
          question: "Do you protect my furniture during the move?",
          answer:
            "Yes, we bring furniture blankets, carrying straps, floor mats and ratchet straps. We protect floors, walls and door frames when carrying out and in. Everything is insured — if anything gets damaged, we compensate for it.",
        },
        {
          question: "Can you help with moving on weekends and evenings?",
          answer:
            "Yes, we offer moving help on weekends and some evenings. Many of our customers prefer to move on a Saturday so they don't have to take time off work. Book online and you'll see all available slots — including weekends.",
        },
        {
          question: "Do you offer moving help for businesses and offices?",
          answer:
            "Absolutely. We carry out office moves and business moves of all sizes — from individual workstations to entire offices. We handle desks, office chairs, filing cabinets, IT equipment and everything else that needs moving. Get in touch for a quote tailored to your needs.",
        },
        {
          question: "Can I get moving help the same week?",
          answer:
            "Yes, we often have availability at short notice. Many customers book a move for the same week, sometimes within just a couple of days. Book online and you'll see straight away which times are available.",
        },
        {
          question: "How should I prepare fragile items for the move?",
          answer:
            "Wrap delicate china, glass and electronics in bubble wrap or newspaper and place them in boxes with cushioning material. Label the boxes \"fragile\" so our team knows to handle them with extra care. If you haven't had time to pack fragile items, we can do it for you — we bring protective materials.",
        },
        {
          question: "Can you store things temporarily during the move?",
          answer:
            "We don't offer our own storage, but we can help you transport items to a storage unit or warehouse if you have one. If your move-in and move-out dates don't align, we can plan the transport in stages so everything ends up in the right place at the right time.",
        },
        {
          question: "Do you offer moving help for long-distance or between cities?",
          answer:
            "Yes, we carry out moves both within and between Stockholm and Gothenburg. For longer moves the price is calculated based on distance and volume. Enter the old and new address when booking and you'll see the cost instantly — whether it's a move within the city or between regions.",
        },
        {
          question: "Which areas do you offer moving help in?",
          answer:
            "We offer moving help throughout the Stockholm region: Botkyrka, Danderyd, Ekerö, Haninge, Huddinge, Järfälla, Lidingö, Nacka, Norrtälje, Nykvarn, Nynäshamn, Salem, Sigtuna, Sollentuna, Solna, Stockholm, Sundbyberg, Södertälje, Tyresö, Täby, Upplands Väsby, Upplands-Bro, Vallentuna, Vaxholm, Värmdö and Österåker. We also operate in the Gothenburg area: Ale, Alingsås, Göteborg, Härryda, Kungälv, Kungsbacka, Lerum, Lilla Edet, Mölndal, Partille, Stenungsund, Tjörn and Öckerö.",
        },
      ],
      priceHint: PACKAGE_PRICE_HINT_EN.transport,
      pricingRows: getPackagePricingRows("transport", "en"),
      dynamicHeadings: {
        threeSteps: "Moving help in 3 easy steps",
        step2Text:
          "Our workers pack, carry everything out, load our van, drive to the new address and carry everything in. You don't need to lift a finger — we handle the entire move.",
        step3Title: "Everything in place",
        step3Desc:
          "We unload and carry everything into your new home. Furniture is placed where you want it and anything we disassembled is reassembled. You can start living in your new home straight away.",
        ctaHeading: "Book moving help today",
        ctaText:
          "Enter the old and new address, describe your household and pick a time. You see the price instantly — van and crew included.",
      },
    },
  },

  {
    // Bohagsflytt — config för /bohagsflytt
    // Vinkel: Flytt av ett komplett privat bohag — hela hemmets innehåll från dörr till dörr.
    // Unik vinkel vs flytthjälp: fokuserar specifikt på privatpersoner som flyttar hela sitt hem (bohag),
    // inte student/kontor/enskilda möbler. Bohag = allt du äger i hemmet.
    slug: "bohagsflytt",
    serviceCategory: "flytt",

    meta: {
      title: "Bohagsflytt – Trygg Flytt av Hela Ditt Hem | Ungdomskraft",
      description:
        "Boka bohagsflytt med skåpbil och personal. Vi flyttar hela ditt bohag – möbler, vitvaror, kartonger och personliga ägodelar. Försäkrat, prisvärt och med unga som gör skillnad.",
      h1: "Bohagsflytt",
    },

    hero: {
      heading: "Trygg och komplett bohagsflytt",
      intro:
        "Ska du flytta hela hemmet? Med vår bohagsflytt tar vi hand om allt ditt bohag – från möbler och vitvaror till kartonger, tavlor och personliga ägodelar. Du får ett team av starka, noggranna ungdomar och en skåpbil som rymmer hela hushållet. Vi packar, bär, kör och ställer allt på plats i ditt nya hem. Oavsett om du bor i en tvåa eller en villa med källare – vi anpassar teamet efter ditt bohags storlek.",
      heroImage: "/hero-flytthjalp.png",
    },

    featureCards: {
      sinceText: "Bohagsflytt sedan 2014",
      storyText:
        "Sedan 2014 har Ungdomskraft hjälpt tusentals privatpersoner att flytta hela sitt bohag tryggt och smidigt. Våra ungdomar är tränade i att hantera allt från ömtåligt porslin till tunga garderober – med omsorg och precision.",
      bookYourText: "Boka din bohagsflytt",
      bookDescText:
        "Ange gamla och nya adressen, beskriv ditt bohag och välj en tid. Du ser priset direkt – skåpbil, personal och försäkring ingår.",
      vehicleTitleText: "Skåpbil dimensionerad för ditt bohag",
      vehicleDescText:
        "Vi väljer rätt fordon utifrån hur mycket bohag du har. Etta eller villa – vi anpassar skåpbilens storlek och antalet bärare så att hela flytten går i en eller så få vändor som möjligt.",
    },

    itemsSection: {
      heading: "Vanligt bohag vi flyttar",
      items: [
        {
          name: "Soffor & fåtöljer",
          description: "Hörnsoffor, bäddsoffor och tunga fåtöljer – vi skyddar och bär varsamt",
          image: "/sofa.webp",
        },
        {
          name: "Sängar & madrasser",
          description: "Dubbelsängar, continental-sängar och resårbottnar",
          image: "/bed.webp",
        },
        {
          name: "Garderober & byråer",
          description: "PAX-garderober, linneskåp, byråer och bokhyllor – demonteras vid behov",
          image: "/wardrobe.webp",
        },
        {
          name: "Vitvaror",
          description: "Kylskåp, frys, tvättmaskin, torktumlare och diskmaskin",
          image: "/fridge.webp",
        },
        {
          name: "Bord & stolar",
          description: "Matbord, matgrupper, skrivbord och soffbord",
          image: "/table.webp",
        },
        {
          name: "Kartonger & personligt",
          description: "Flyttkartonger med kläder, böcker, kök, tavlor och personliga ägodelar",
          image: "/box.webp",
        },
      ],
    },

    seoContent: {
      mainHeading: "Bohagsflytt – hela ditt hem flyttat tryggt och smidigt",
      mainText:
        "Bohagsflytt innebär att hela ditt hems innehåll flyttas från en adress till en annan – möbler, vitvaror, kartonger, tavlor, lampor, mattor och allt annat som gör ett hem till ett hem. Det är den mest kompletta formen av flytt för privatpersoner, och det är precis det vi är specialiserade på. Med Ungdomskrafts bohagsflytt får du ett dedikerat team som tar hand om hela processen: vi packar det som inte redan sitter i kartonger, demonterar möbler som behöver tas isär, bär ut allt från alla rum, lastar skåpbilen smart och säkert, kör till nya bostaden och bär in allt på rätt plats. Sedan monterar vi ihop möblerna, ställer vitvarorna där de ska stå och ser till att du kan börja leva i ditt nya hem direkt. Allt ingår i bokningen – fordon, personal, skyddsmaterial och försäkring. Du behöver inte hyra bil, köra själv eller ringa runt efter vänner. Vi dimensionerar teamet och fordonet efter hur stort ditt bohag är, så att flytten går effektivt och utan onödiga väntetider. Många av våra kunder väljer bohagsflytt för att det är enklare, tryggare och ofta billigare än att försöka organisera allting själv.",
      preparationHeading: "Förbered inför din bohagsflytt",
      preparationText:
        "Packa rum för rum och märk varje kartong med innehåll och vilket rum den ska till i nya bostaden. Börja med rum du använder minst – gästrum, förråd och garderober. Töm kylskåp och frys minst ett dygn innan flytten och koppla loss tvättmaskinen. Demontera möbler som inte går genom dörrar hela: IKEA-garderober, stora sängramar och matbord. Samla alla skruvar och beslag i märkta påsar och tejpa dem på möbeln. Se till att trapphus och hiss är fria och att parkering för skåpbilen är ordnad vid båda adresserna. Ha nycklar redo till både gamla och nya bostaden och meddela grannar att det blir lite extra trafik i trapphuset.",
      additionalSections: [
        {
          heading: "Bohagsflytt från lägenhet",
          content:
            "De flesta bohagsflytt vi utför är från lägenheter – allt från ettor till stora fyror. I en lägenhet handlar det ofta om att navigera trånga trapphus, tunga möbler genom smala dörrar och koordinera hissanvändning med grannar. Våra team är vana vid alla typer av trapphus och lägenhetsplanlösningar. Vi skyddar golv, väggar och dörrkarmar med mattor och skydd, och vi planerar bärningen så att vi arbetar effektivt utan att skada fastigheten. Bor du högt upp utan hiss? Inga problem – vi har erfarenhet av att bära hela bohag genom trappor i flervåningshus. Teamet anpassas efter antal trappor och mängden bohag.",
        },
        {
          heading: "Bohagsflytt från villa och radhus",
          content:
            "En villaflytt innebär ofta ett större bohag utspritt på flera våningar, källare och ibland garage. Det kan handla om tunga träsoffor, kassaskåp, verktyg, trädgårdsmöbler och säsongsprylar som skidor och cyklar. Vi skalar upp teamet vid villaflytt – fler bärare, större skåpbil och om det behövs flera vändor. Vi planerar hela flytten i förväg så att vi vet vilka rum som ska tömmas först, vilka möbler som behöver demonteras och hur vi lastar mest effektivt. Resultatet är en smidig flytt utan stress, oavsett hur mycket bohag du har.",
        },
        {
          heading: "Vad ingår i en bohagsflytt?",
          listItems: [
            {
              label: "Skåpbil",
              text: "Vi kommer med rätt storlek på fordon, anpassat efter ditt bohags volym.",
            },
            {
              label: "Packning",
              text: "Vi packar det som inte redan sitter i kartonger – kök, kläder, böcker, småprylar och ömtåligt.",
            },
            {
              label: "Demontering",
              text: "Garderober, sängramar, bord och hyllor som behöver tas isär innan de bärs ut.",
            },
            {
              label: "Bärning & lastning",
              text: "Vi bär ut alla möbler, vitvaror och kartonger och lastar skåpbilen smart och säkert.",
            },
            {
              label: "Transport",
              text: "Vi kör hela sträckan till din nya bostad – du behöver inte följa med om du inte vill.",
            },
            {
              label: "Lossning, inbärning & montering",
              text: "Vi lossar, bär in allt, ställer möbler och vitvaror på plats och monterar det som tagits isär.",
            },
          ],
          afterContent:
            "Du väljer vilka moment du behöver hjälp med. Vill du packa själv och bara ha bärning och transport? Det funkar utmärkt.",
        },
        {
          heading: "Varför boka bohagsflytt med Ungdomskraft?",
          content:
            "Ungdomskraft är inte en vanlig flyttfirma. Vi är ett socialt företag som sysselsätter ungdomar och ger dem riktig arbetslivserfarenhet – och det märks i varje bohagsflytt vi genomför. När du bokar bohagsflytt hos oss får du ett team av motiverade, energiska unga människor som verkligen vill leverera ett felfritt jobb. Varje flytt är för dem en möjlighet att visa vad de går för, och den inställningen genomsyrar allt – från hur de hanterar ditt ömtåligaste porslin till hur de ställer den sista kartongen på rätt plats.\n\nSedan starten 2014 har vi genomfört tusentals bohagsflytt i Stockholms- och Göteborgsområdet. Under den resan har vi gett tusentals ungdomar deras första riktiga arbetslivserfarenhet – med allt vad det innebär: att komma i tid, kommunicera professionellt med kunder, lösa problem på plats, samarbeta i team och ta ansvar för resultatet. Våra medarbetare lär sig packningsteknik, lyftteknik, säker lastning och hur man hanterar allt från bräckligt glas till 200-kilos kassaskåp. De tränas i att skydda kundens ägodelar, golv, väggar och dörrkarmar – och i att alltid lämna både gamla och nya bostaden i gott skick.\n\nDet här är inte bara bra för ungdomarna – det är bra för dig som kund. Du märker skillnaden i engagemang och energi. Våra team springer där andra går, dubbelkollar att allt bohag är rätt packat och frågar alltid om det finns något mer de kan hjälpa till med. Vi har byggt en kultur där varje bohagsflytt ses som en chans att överträffa förväntningarna, och det syns i de hundratals femstjärniga recensioner vi fått på Trustpilot och Google.\n\nMen det handlar inte bara om kvalitet – det handlar om mening. Varje bohagsflytt du bokar genom Ungdomskraft bidrar direkt till att en ung person får arbetslivserfarenhet, lär sig ett yrke och bygger sitt cv. Många av våra medarbetare går vidare till fasta anställningar inom logistik, transport eller serviceyrken – och de bär med sig de värderingar och den arbetsmoral de lärt sig hos oss. Att välja oss innebär att din bohagsflytt blir en del av något större.\n\nVi tar också hållbarhet på allvar. Möbler och bohag som inte ska med till nya hemmet sorterar vi och kör till rätt återvinningsstation eller skänker till välgörenhetsorganisationer som Myrorna, Erikshjälpen och Stadsmissionen. Ingenting slängs i onödan. Företagskunder som rapporterar CSR kan räkna in samarbetet med oss som ett konkret bidrag till ungdomssysselsättning och cirkulär ekonomi.\n\nDet är trygg bohagsflytt med mening.\n\nBohagsflytt är en RUT-berättigad tjänst. Du får avdrag på 50 % av arbetskostnaden – upp till 75 000 kr per person och år. Avdraget hanteras direkt på fakturan, så du betalar bara halva priset för arbetet.",
        },
      ],
    },

    faq: [
      {
        question: "Vad innebär bohagsflytt?",
        answer:
          "Bohagsflytt innebär att hela ditt hems innehåll flyttas – möbler, vitvaror, kartonger, tavlor, lampor, mattor och allt annat som ingår i ditt bohag. Vi tar hand om packning, demontering, bärning, transport, lossning, inbärning och montering.",
      },
      {
        question: "Hur mycket kostar en bohagsflytt?",
        answer:
          "Priset för bohagsflytt beror på bohagts storlek, avstånd och antal bärare. En tvåa med normalt bohag landar ofta på 1 495–2 495 kr, en villa med fullt bohag på 2 495–4 995 kr eller mer. Du ser priset direkt vid bokning.",
      },
      {
        question: "Kan jag få RUT-avdrag på bohagsflytt?",
        answer:
          "Absolut, bohagsflytt ger rätt till RUT-avdrag. Du får avdrag på 50 % av arbetskostnaden, upp till 75 000 kr per person och år. Avdraget gäller arbete kopplat till flytt av din privatbostad – packning, demontering, bärning, transport och montering. Vi hanterar RUT-avdraget direkt på fakturan, så du betalar bara halva arbetskostnaden.",
      },
      {
        question: "Hur snabbt kan ni genomföra en bohagsflytt?",
        answer:
          "Vi erbjuder ofta bohagsflytt med kort varsel – ibland redan samma vecka. Boka online så ser du tillgängliga tider direkt. Vanligtvis räcker en dag för de flesta bohag.",
      },
      {
        question: "Behöver jag packa allt själv innan bohagsflytten?",
        answer:
          "Nej, vi kan hjälpa till med packning. Men ju mer du packar själv i förväg, desto snabbare och billigare blir bohagsflytten. Märk kartongerna med rum så att vi kan ställa dem direkt på rätt plats i nya bostaden.",
      },
      {
        question: "Ingår skåpbil i bohagsflytten?",
        answer:
          "Ja, skåpbil ingår alltid i vår bohagsflytt. Vi väljer rätt storlek utifrån ditt bohag – du behöver inte hyra, köra eller tanka. Allt sköts av oss.",
      },
      {
        question: "Kan ni bohagsflytta vitvaror som kylskåp och tvättmaskin?",
        answer:
          "Ja, alla vitvaror ingår i bohagsflytten. Vi kopplar loss, säkrar och transporterar kylskåp, frys, tvättmaskin, torktumlare och diskmaskin. Vi kan ansluta dem igen på nya adressen om du vill.",
      },
      {
        question: "Skyddar ni möblerna under bohagsflytten?",
        answer:
          "Ja, vi har möbelfiltar, bärremmar, skyddsmattor och spännband. Vi skyddar golv, väggar och dörrkarmar vid bärning. Allt bohag är försäkrat – om något skadas ersätter vi det.",
      },
      {
        question: "Hur många bärare behövs för en bohagsflytt?",
        answer:
          "Vi anpassar teamet efter ditt bohags storlek. En tvåa behöver oftast två bärare. En fyra eller villa med stort bohag kan kräva tre till fyra personer. Beskriv ditt bohag vid bokning så rekommenderar vi rätt antal.",
      },
      {
        question: "Kan ni bohagsflytta på helger och kvällar?",
        answer:
          "Ja, vi erbjuder bohagsflytt även på helger och vissa kvällar. Många kunder föredrar lördag för att slippa ta ledigt. Boka online och se alla tillgängliga tider.",
      },
      {
        question: "Demonterar och monterar ni möbler vid bohagsflytt?",
        answer:
          "Ja, vi demonterar garderober, sängramar, bord och hyllor som behöver tas isär för att kunna bäras ut. På nya adressen monterar vi ihop allt igen. Spara gärna skruvar och beslag i märkta påsar.",
      },
      {
        question: "Kan jag välja vilka delar av bohagsflytten jag vill ha hjälp med?",
        answer:
          "Absolut. Du kan boka komplett bohagsflytt med packning, bärning, transport och montering – eller bara de delar du behöver. Vill du packa själv och bara ha bärning och transport? Det funkar utmärkt.",
      },
      {
        question: "Hur förbereder jag ömtåliga saker inför bohagsflytten?",
        answer:
          "Linda ömtåligt porslin, glas och elektronik i bubbelplast eller tidningspapper och lägg dem i kartonger med stötdämpande material. Märk kartongerna med \"ömtåligt\". Har du inte hunnit kan vi packa ömtåligt åt dig – vi har skyddsmaterial med oss.",
      },
      {
        question: "Erbjuder ni bohagsflytt mellan Stockholm och Göteborg?",
        answer:
          "Ja, vi utför bohagsflytt både inom och mellan Stockholm och Göteborg. Priset beräknas utifrån avstånd och mängd bohag. Ange adresserna vid bokning så ser du kostnaden direkt.",
      },
      {
        question: "I vilka kommuner erbjuder ni bohagsflytt?",
        answer:
          "Vi erbjuder bohagsflytt i hela Stockholmsområdet: Botkyrka, Danderyd, Ekerö, Haninge, Huddinge, Järfälla, Lidingö, Nacka, Norrtälje, Nykvarn, Nynäshamn, Salem, Sigtuna, Sollentuna, Solna, Stockholm, Sundbyberg, Södertälje, Tyresö, Täby, Upplands Väsby, Upplands-Bro, Vallentuna, Vaxholm, Värmdö och Österåker. Vi finns även i Göteborgsområdet: Ale, Alingsås, Göteborg, Härryda, Kungälv, Kungsbacka, Lerum, Lilla Edet, Mölndal, Partille, Stenungsund, Tjörn och Öckerö.",
      },
    ],

    priceHint: PACKAGE_PRICE_HINT_SV.transport,

    pricingRows: getPackagePricingRows("transport", "sv"),

    dynamicHeadings: {
      threeSteps: "Bohagsflytt i 3 enkla steg",
      step2Text:
        "Våra ungdomar packar, demonterar möbler, bär ut hela ditt bohag, lastar skåpbilen, kör till nya adressen och bär in allt på rätt plats. Du behöver inte lyfta ett finger.",
      step3Title: "Allt på plats",
      step3Desc:
        "Vi lossar och bär in hela ditt bohag i det nya hemmet. Möbler, vitvaror och kartonger ställs på rätt plats – och det som demonterats monteras ihop igen.",
      ctaHeading: "Boka bohagsflytt idag",
      ctaText:
        "Ange gamla och nya adressen, beskriv ditt bohag och välj en tid. Du ser priset direkt – skåpbil, personal och försäkring ingår.",
    },

    en: {
      slug: "household-moving",
      meta: {
        title: "Household Moving – Full Home Relocation Service | Ungdomskraft",
        description:
          "Book household moving with van and crew in Stockholm & Gothenburg. We move furniture, appliances, boxes and belongings – insured, fixed price. Book today!",
        h1: "Household moving",
      },
      hero: {
        heading: "Safe and complete household moving",
        intro:
          "Moving your entire home? Our household moving service takes care of everything you own — furniture, appliances, boxes, artwork and personal belongings. You get a team of strong, meticulous young workers and a van sized for your household. We pack, carry, drive and put everything in place at your new home. Whether you live in a one-bed flat or a house with a basement — we scale the team to match your household.",
      },
      featureCards: {
        sinceText: "Household moving since 2014",
        bookYourText: "Book your household move",
        bookDescText:
          "Enter the old and new address, describe your household and pick a time. You see the price instantly — van, crew and insurance included.",
        vehicleTitleText: "Van sized for your household",
        vehicleDescText:
          "We choose the right vehicle based on how much you own. Flat or house — we match the van size and number of carriers so the entire move runs in as few trips as possible.",
      },
      itemsSection: {
        heading: "Common household items we move",
        items: [
          {
            name: "Sofas & armchairs",
            description: "Corner sofas, sofa beds and heavy armchairs — protected and carried with care",
            image: "/sofa.webp",
          },
          {
            name: "Beds & mattresses",
            description: "Double beds, continental beds and sprung bases",
            image: "/bed.webp",
          },
          {
            name: "Wardrobes & dressers",
            description: "PAX wardrobes, linen cupboards, dressers and bookcases — disassembled if needed",
            image: "/wardrobe.webp",
          },
          {
            name: "Appliances",
            description: "Fridges, freezers, washing machines, tumble dryers and dishwashers",
            image: "/fridge.webp",
          },
          {
            name: "Tables & chairs",
            description: "Dining tables, dining sets, desks and coffee tables",
            image: "/table.webp",
          },
          {
            name: "Boxes & personal items",
            description: "Moving boxes with clothes, books, kitchenware, artwork and personal belongings",
            image: "/box.webp",
          },
        ],
      },
      seoContent: {
        mainHeading: "Household moving — your entire home relocated safely and smoothly",
        mainText:
          "Household moving means relocating the full contents of your home from one address to another — furniture, appliances, boxes, artwork, lamps, rugs and everything else that makes a home a home. It is the most complete form of private moving, and it is exactly what we specialise in. With Ungdomskraft's household moving service you get a dedicated team that handles the entire process: we pack what isn't already in boxes, disassemble furniture that needs taking apart, carry everything out from every room, load the van smartly and securely, drive to the new home and carry everything in. Then we reassemble furniture, position appliances and make sure you can start living in your new home straight away. Everything is included in the booking — vehicle, crew, protective materials and insurance. You don't need to rent a vehicle, drive yourself or call around for friends. We size the team and vehicle to match how large your household is, so the move runs efficiently without unnecessary waiting. Many of our customers choose household moving because it is simpler, safer and often cheaper than trying to organise everything on their own.",
        preparationHeading: "Preparing for your household move",
        preparationText:
          "Pack room by room and label every box with its contents and the room it belongs to in the new home. Start with rooms you use least — guest rooms, storage and wardrobes. Empty the fridge and freezer at least a day before the move and disconnect the washing machine. Disassemble furniture that won't fit through doorways whole: IKEA wardrobes, large bed frames and dining tables. Collect all screws and fittings in labelled bags and tape them to the furniture. Make sure stairwells and lifts are clear and parking for the van is arranged at both addresses. Have keys to both homes ready and let neighbours know there will be extra activity in the stairwell.",
        additionalSections: [
          {
            heading: "Household moving from a flat",
            content:
              "Most household moves we carry out are from flats — anything from studios to large four-beds. In a flat, the challenge is often navigating narrow stairwells, manoeuvring heavy furniture through tight doorways and coordinating lift use with neighbours. Our teams are experienced with every type of stairwell and floor plan. We protect floors, walls and door frames with mats and padding, and we plan the carrying order so we work efficiently without damaging the property. Living high up with no lift? No problem — we have experience carrying entire households down multiple flights of stairs. The team is scaled to match the number of floors and the size of your household.",
          },
          {
            heading: "Household moving from a house",
            content:
              "A house move usually means a larger household spread across multiple floors, a basement and sometimes a garage. It can involve heavy wooden sofas, safes, tools, garden furniture and seasonal items like skis and bicycles. We scale the team up for house moves — more carriers, a larger van and multiple trips if needed. We plan the entire move in advance so we know which rooms to clear first, which furniture needs disassembling and how to load most efficiently. The result is a smooth, stress-free move, no matter how much you own.",
          },
          {
            heading: "What's included in household moving?",
            listItems: [
              {
                label: "Van",
                text: "We arrive with the right size vehicle, matched to the volume of your household.",
              },
              {
                label: "Packing",
                text: "We pack what isn't already in boxes — kitchen, clothes, books, small items and fragile goods.",
              },
              {
                label: "Disassembly",
                text: "Wardrobes, bed frames, tables and shelves that need taking apart before carrying out.",
              },
              {
                label: "Carrying & loading",
                text: "We carry all furniture, appliances and boxes out and load the van smartly and securely.",
              },
              {
                label: "Transport",
                text: "We drive the full distance to your new home — you don't need to ride along if you'd rather go ahead.",
              },
              {
                label: "Unloading, carrying in & assembly",
                text: "We unload, carry everything in, position furniture and appliances and reassemble what was taken apart.",
              },
            ],
            afterContent:
              "You choose which parts you need help with. Want to pack yourself and just have carrying and transport? That works perfectly.",
          },
          {
            heading: "Why book household moving with Ungdomskraft?",
            content:
              "Ungdomskraft is not an ordinary removal company. We are a social enterprise that employs young people and gives them real work experience — and it shows in every household move we carry out. When you book household moving with us, you get a team of motivated, energetic young workers who genuinely want to deliver a flawless job. Every move is a chance for them to prove themselves, and that mindset runs through everything — from how they handle your most delicate china to how they place the last box in exactly the right spot.\n\nSince our founding in 2014, we have carried out thousands of household moves across the Stockholm and Gothenburg regions. Along the way, we have given thousands of young people their first real work experience — with everything that entails: turning up on time, communicating professionally with customers, solving problems on-site, collaborating as a team and taking responsibility for the result. Our workers learn packing techniques, lifting techniques, safe loading and how to handle everything from fragile glassware to 200-kilo safes. They are trained to protect the customer's belongings, floors, walls and door frames — and to always leave both the old and new home in good condition.\n\nThis is not just good for the young people — it is good for you as a customer. You notice the difference in commitment and energy. Our teams run where others walk, double-check that all household items are properly packed and always ask if there is anything else they can help with. We have built a culture where every household move is seen as an opportunity to exceed expectations, and it shows in the hundreds of five-star reviews we have received on Trustpilot and Google.\n\nBut it is not just about quality — it is about meaning. Every household move you book through Ungdomskraft directly contributes to a young person gaining work experience, learning a trade and building their CV. Many of our workers go on to permanent positions in logistics, transport or service industries — carrying with them the values and work ethic they learned with us. Choosing us means your household move becomes part of something bigger.\n\nWe also take sustainability seriously. Furniture and household items that are not coming to the new home are sorted and taken to the right recycling station or donated to charities such as Myrorna, Erikshjälpen and Stadsmissionen. Nothing is dumped unnecessarily. Corporate customers reporting CSR can count their collaboration with us as a tangible contribution to youth employment and the circular economy.\n\nIt is safe household moving with purpose.\n\nHousehold moving is eligible for the Swedish RUT tax deduction. You get a 50% deduction on the labour cost – up to SEK 75,000 per person per year. The deduction is handled directly on the invoice, so you only pay half the price for the work.",
          },
        ],
      },
      faq: [
        {
          question: "What does household moving include?",
          answer:
            "Household moving means relocating the full contents of your home — furniture, appliances, boxes, artwork, lamps, rugs and everything else. We handle packing, disassembly, carrying, transport, unloading, carrying in and furniture assembly.",
        },
        {
          question: "How much does household moving cost?",
          answer:
            "The price depends on the size of your household, distance and number of carriers. A one- or two-bed with normal contents often comes to SEK 1,495–2,495. A house with a full household can come to SEK 2,495–4,995 or more. You see the price instantly when booking.",
        },
        {
          question: "Can I get a RUT tax deduction on household moving?",
          answer:
            "Absolutely, household moving qualifies for the Swedish RUT tax deduction. You get a 50% deduction on the labour cost, up to SEK 75,000 per person per year. The deduction covers work related to moving your private residence – packing, disassembly, carrying, transport and assembly. We handle the RUT deduction directly on the invoice, so you only pay half the labour cost.",
        },
        {
          question: "How quickly can you carry out a household move?",
          answer:
            "We often offer household moving at short notice — sometimes the same week. Book online and you'll see available times straight away. Most households can be moved in a single day.",
        },
        {
          question: "Do I need to pack everything myself before the household move?",
          answer:
            "No, we can help with packing. But the more you pack in advance, the faster and cheaper the household move will be. Label boxes by room so we can place them in the right spot straight away.",
        },
        {
          question: "Is a van included in the household move?",
          answer:
            "Yes, a van is always included. We choose the right size based on your household — you don't need to rent, drive or refuel. Everything is handled by us.",
        },
        {
          question: "Can you move appliances like fridges and washing machines?",
          answer:
            "Yes, all appliances are included in the household move. We disconnect, secure and transport fridges, freezers, washing machines, tumble dryers and dishwashers. We can reconnect them at the new address if you wish.",
        },
        {
          question: "Do you protect furniture during the household move?",
          answer:
            "Yes, we bring furniture blankets, carrying straps, floor mats and ratchet straps. We protect floors, walls and door frames when carrying. All household items are insured — if anything gets damaged, we compensate for it.",
        },
        {
          question: "How many carriers are needed for a household move?",
          answer:
            "We scale the team to match your household. A one- or two-bed usually needs two carriers. A three-bed or house with a large household may need three to four. Describe your household when booking and we'll recommend the right number.",
        },
        {
          question: "Can you do household moving on weekends and evenings?",
          answer:
            "Yes, we offer household moving on weekends and some evenings. Many customers prefer Saturday so they don't have to take time off work. Book online and you'll see all available slots.",
        },
        {
          question: "Do you disassemble and reassemble furniture during household moves?",
          answer:
            "Yes, we disassemble wardrobes, bed frames, tables and shelves that need taking apart. At the new address we reassemble everything. Keep screws and fittings in labelled bags for a smoother process.",
        },
        {
          question: "Can I choose which parts of the household move I need help with?",
          answer:
            "Absolutely. You can book a complete household move with packing, carrying, transport and assembly — or just the parts you need. Want to pack yourself and only have carrying and transport? That works perfectly.",
        },
        {
          question: "How should I prepare fragile items for the household move?",
          answer:
            "Wrap delicate china, glass and electronics in bubble wrap or newspaper and place them in boxes with cushioning material. Label the boxes \"fragile\". If you haven't had time, we can pack fragile items for you — we bring protective materials.",
        },
        {
          question: "Do you offer household moving between Stockholm and Gothenburg?",
          answer:
            "Yes, we carry out household moves both within and between Stockholm and Gothenburg. The price is based on distance and volume. Enter both addresses when booking and you'll see the cost instantly.",
        },
        {
          question: "Which areas do you offer household moving in?",
          answer:
            "We offer household moving throughout the Stockholm region: Botkyrka, Danderyd, Ekerö, Haninge, Huddinge, Järfälla, Lidingö, Nacka, Norrtälje, Nykvarn, Nynäshamn, Salem, Sigtuna, Sollentuna, Solna, Stockholm, Sundbyberg, Södertälje, Tyresö, Täby, Upplands Väsby, Upplands-Bro, Vallentuna, Vaxholm, Värmdö and Österåker. We also operate in the Gothenburg area: Ale, Alingsås, Göteborg, Härryda, Kungälv, Kungsbacka, Lerum, Lilla Edet, Mölndal, Partille, Stenungsund, Tjörn and Öckerö.",
        },
      ],
      priceHint: PACKAGE_PRICE_HINT_EN.transport,
      pricingRows: getPackagePricingRows("transport", "en"),
      dynamicHeadings: {
        threeSteps: "Household moving in 3 easy steps",
        step2Text:
          "Our workers pack, disassemble furniture, carry your entire household out, load the van, drive to the new address and carry everything in. You don't need to lift a finger.",
        step3Title: "Everything in place",
        step3Desc:
          "We unload and carry your entire household into the new home. Furniture, appliances and boxes are placed where they belong — and anything we disassembled is reassembled.",
        ctaHeading: "Book household moving today",
        ctaText:
          "Enter the old and new address, describe your household and pick a time. You see the price instantly — van, crew and insurance included.",
      },
    },
  },

  {
    // Flytt företag — config för /flytt-foretag
    // Vinkel: Företagsflytt / kontorsflytt — flytta hela verksamheten från ett kontor till ett annat.
    // Unik vinkel vs tömma-kontor: här flyttas allt TILL ett nytt kontor (relocation), inte tömning/bortforsling.
    // Unik vinkel vs flytthjälp/bohagsflytt: riktar sig till företag, inte privatpersoner.
    slug: "flytt-foretag",
    serviceCategory: "flytt",

    meta: {
      title: "Flytt Företag – Smidig Kontors- & Företagsflytt | Ungdomskraft",
      description:
        "Boka företagsflytt i Stockholm och Göteborg. Vi flyttar kontor, lager och verksamheter – skrivbord, IT och möbler. Kvällar och helger. Fast pris!",
      h1: "Företagsflytt",
    },

    hero: {
      heading: "Professionell företagsflytt",
      intro:
        "Ska företaget byta lokaler? Vi flyttar hela verksamheten – skrivbord, kontorsstolar, IT-utrustning, arkivskåp och allt annat som behöver följa med till det nya kontoret. Med skåpbil och ett team av starka, noggranna ungdomar genomför vi flytten snabbt och utan störningar i er verksamhet. Vi erbjuder företagsflytt på kvällar och helger så att ni kan fortsätta jobba som vanligt under dagen.",
      heroImage: "/hero-flytthjalp.png",
    },

    featureCards: {
      sinceText: "Företagsflytt sedan 2014",
      storyText:
        "Sedan 2014 har Ungdomskraft hjälpt hundratals företag att flytta kontor, lager och verksamheter. Våra ungdomar är tränade i att hantera kontorsmöbler, IT-utrustning och känsliga dokument – snabbt, varsamt och professionellt.",
      bookYourText: "Boka er företagsflytt",
      bookDescText:
        "Ange gamla och nya adressen, beskriv vad som ska flyttas och välj en tid. Vi erbjuder kvällar och helger. Ni ser priset direkt.",
      vehicleTitleText: "Skåpbil anpassad för kontorsflytt",
      vehicleDescText:
        "Vi kommer med rätt fordon för er verksamhet. Från ett enskilt kontorsrum till hela kontorsplan – vi dimensionerar skåpbil och team efter era behov och ser till att allt transporteras säkert.",
    },

    itemsSection: {
      heading: "Vanligt vi flyttar vid företagsflytt",
      items: [
        {
          name: "Skrivbord & arbetsstationer",
          description: "Höj- och sänkbara skrivbord, skrivbordsstolar och arbetsplatsutrustning",
          image: "/office-desk.webp",
        },
        {
          name: "Kontorsstolar",
          description: "Ergonomiska kontorsstolar, konferensstolar och besöksstolar",
          image: "/armchair.webp",
        },
        {
          name: "Arkivskåp & bokhyllor",
          description: "Dokumentskåp, arkivhyllor, bokhyllor och kassaskåp",
          image: "/wardrobe.webp",
        },
        {
          name: "IT-utrustning",
          description: "Skärmar, datorer, skrivare, kopiatorer och servrar",
          image: "/copy-machine.webp",
        },
        {
          name: "Konferensrum & möbler",
          description: "Konferensbord, whiteboards, projektorer och loungemöbler",
          image: "/table.webp",
        },
        {
          name: "Kontorskök & pentry",
          description: "Kylskåp, mikrovågsugn, kaffemaskin och köksutrustning",
          image: "/mini-fridge.webp",
        },
      ],
    },

    seoContent: {
      mainHeading: "Företagsflytt – hela verksamheten flyttad smidigt och utan avbrott",
      mainText:
        "En företagsflytt handlar om mer än att bära möbler. Det handlar om att flytta en hel verksamhet – med minimal störning i det dagliga arbetet. Med Ungdomskrafts företagsflytt får ni ett dedikerat team som planerar och genomför hela flytten: vi packar kontor, demonterar skrivbord och hyllsystem, bär ut allt, lastar skåpbilen, kör till de nya lokalerna och ställer upp allt på rätt plats. IT-utrustning hanteras varsamt, känsliga dokument och arkiv packas separat, och vi ser till att det nya kontoret är redo att arbeta i när ni kommer på måndag morgon. Vi erbjuder företagsflytt på kvällar, helger och under semesterperioder – tider som minimerar störningen för er och era medarbetare. Allt ingår i en bokning: fordon, personal, skyddsmaterial och försäkring. Oavsett om ni flyttar ett litet kontor med fem arbetsplatser eller en hel kontorsbyggnad med flera plan anpassar vi teamets storlek och logistiken efter er verksamhets behov.",
      preparationHeading: "Förbered inför företagsflytten",
      preparationText:
        "Informera alla medarbetare i god tid och utse en kontaktperson som koordinerar med vårt team. Låt varje medarbetare packa sitt eget skrivbord i märkta kartonger – det sparar tid och minskar risken att saker blandas. IT-avdelningen bör koppla loss och märka all utrustning: skärmar, stationära datorer, kablar och kringutrustning. Rensa ur gemensamma ytor som fikarum, förråd och arkiv – det som inte ska med behöver inte flyttas. Boka hiss och lastzon vid båda adresserna och meddela fastighetsägaren om flytten. Se till att nycklar och passerkort till de nya lokalerna är redo.",
      additionalSections: [
        {
          heading: "Litet kontor och startup-flytt",
          content:
            "Flyttar ni ett mindre kontor med 5–15 arbetsplatser? Då behöver ni inte en stor flyttfirma med tre lastbilar. Vår startup-lösning är anpassad för snabba, effektiva kontorsflytt: ett team på två till tre personer och en skåpbil som rymmer allt. Vi flyttar skrivbord, stolar, skärmar, kartonger och det lilla pentryt – ofta på en enda kväll eller helgdag. Priset är fast och ni ser det vid bokning. Perfekt för startup-bolag, konsultfirmor och kontorshotell.",
        },
        {
          heading: "Stor kontorsflytt och flera plan",
          content:
            "Vid en större företagsflytt – hela våningsplan, flera avdelningar eller en hel kontorsbyggnad – skalar vi upp teamet. Fler bärare, större fordon och en detaljerad logistikplan som säkerställer att allt hamnar på rätt plats i de nya lokalerna. Vi kan arbeta parallellt på flera plan, märka och katalogisera inventarier och koordinera med er IT-avdelning för att minimera den tid era system är nere. Tunga kassaskåp, serverskåp och specialutrustning hanteras med rätt teknik och utrustning. Vi genomför ofta stora kontorsflytt i etapper – till exempel avdelning för avdelning under flera kvällar – så att verksamheten aldrig stannar helt.",
        },
        {
          heading: "Vad ingår i en företagsflytt?",
          listItems: [
            {
              label: "Skåpbil",
              text: "Vi kommer med rätt fordon, anpassat efter kontorsytans storlek och mängden inventarier.",
            },
            {
              label: "Packning & märkning",
              text: "Vi packar det som inte redan är i kartonger och märker allt med avdelning och rum.",
            },
            {
              label: "Demontering",
              text: "Skrivbord, hyllsystem, arkivskåp och möbler som behöver tas isär före transport.",
            },
            {
              label: "Bärning & lastning",
              text: "Vi bär ut alla möbler, IT-utrustning och kartonger och lastar skåpbilen säkert.",
            },
            {
              label: "Transport",
              text: "Vi kör hela sträckan till de nya lokalerna – oavsett om det är i samma stadsdel eller en annan stad.",
            },
            {
              label: "Uppställning & montering",
              text: "Vi lossar, bär in, placerar möbler och utrustning på rätt plats och monterar det som tagits isär.",
            },
          ],
          afterContent:
            "Ni väljer vilka moment ni behöver. Vill ni bara ha transport och bärning? Det funkar utmärkt.",
        },
        {
          heading: "Varför boka företagsflytt med Ungdomskraft?",
          content:
            "Ungdomskraft är inte en vanlig flyttfirma. Vi är ett socialt företag som sysselsätter ungdomar och ger dem riktig arbetslivserfarenhet – och det märks i varje företagsflytt vi genomför. När ni bokar företagsflytt hos oss får ni ett team av motiverade, energiska unga människor som verkligen vill leverera ett professionellt resultat. Varje uppdrag är för dem en chans att visa vad de går för, och den inställningen genomsyrar hela processen – från hur de hanterar er IT-utrustning till hur de ställer den sista stolen på plats i det nya kontoret.\n\nSedan starten 2014 har vi genomfört hundratals kontors- och företagsflytt i Stockholms- och Göteborgsområdet. Under den resan har vi gett tusentals ungdomar deras första riktiga arbetslivserfarenhet – med allt vad det innebär: att komma i tid, kommunicera professionellt med kunder och fastighetsägare, lösa problem på plats, samarbeta i team och ta ansvar för resultatet. Våra medarbetare lär sig lyftteknik, säker lastning, hantering av känslig utrustning och hur man genomför en flytt med minimal störning i en pågående verksamhet. De tränas i att skydda inventarier, golv, väggar och dörrkarmar – och i att alltid lämna både gamla och nya lokaler i gott skick.\n\nDet här är inte bara bra för ungdomarna – det är bra för er som företag. Ni märker skillnaden i engagemang och energi. Våra team arbetar strukturerat och snabbt, dubbelkollar att allt är rätt packat och märkt, och frågar alltid om det finns något mer de kan hjälpa till med. Vi har byggt en kultur där varje företagsflytt ses som en möjlighet att överträffa förväntningarna, och det syns i de hundratals femstjärniga recensioner vi fått på Trustpilot och Google.\n\nMen det handlar inte bara om kvalitet – det handlar om mening. Varje företagsflytt ni bokar genom Ungdomskraft bidrar direkt till att en ung person får arbetslivserfarenhet, lär sig ett yrke och bygger sitt cv. Många av våra medarbetare går vidare till fasta anställningar inom logistik, transport eller serviceyrken – och de bär med sig de värderingar och den arbetsmoral de lärt sig hos oss. Att välja oss innebär att er företagsflytt blir en del av något större – och ett konkret bidrag till ungdomssysselsättning.\n\nVi tar också hållbarhet på allvar. Kontorsmöbler och utrustning som inte ska med till de nya lokalerna sorterar vi och kör till rätt återvinningsstation eller skänker till välgörenhetsorganisationer. Ingenting slängs i onödan. Som företagskund kan ni räkna in samarbetet med oss i er CSR-rapportering som ett konkret bidrag till ungdomssysselsättning och cirkulär ekonomi.\n\nDet är professionell företagsflytt med mening.\n\nRUT-avdrag gäller flytt av privatbostäder, inte kontorslokaler. Om en företagsflytt inkluderar flytt av en anställds bostad kan den delen ge rätt till RUT-avdrag med 50 % av arbetskostnaden. Vi hjälper gärna till att dela upp fakturan vid behov.",
        },
      ],
    },

    faq: [
      {
        question: "Vad ingår i en företagsflytt med Ungdomskraft?",
        answer:
          "Allt ni behöver: skåpbil, personal, packning, demontering av skrivbord och hyllsystem, bärning, lastning, transport till de nya lokalerna, lossning, inbärning, uppställning och montering. Ni väljer vilka moment ni behöver.",
      },
      {
        question: "Kan ni genomföra företagsflytten på kvällar eller helger?",
        answer:
          "Ja, vi erbjuder företagsflytt på kvällar, helger och under semesterperioder. Det är det vanligaste – de flesta företag vill minimera störningen i verksamheten och flytta när medarbetarna inte är på plats.",
      },
      {
        question: "Hur mycket kostar en företagsflytt?",
        answer:
          "Priset beror på mängden inventarier, avstånd och antal bärare. Ett litet kontor med 5–10 arbetsplatser landar ofta på 1 495–2 495 kr. Större kontorsflytt med 20+ arbetsplatser kan landa på 2 495–4 995 kr eller mer. Ni ser priset direkt vid bokning.",
      },
      {
        question: "Kan vi få RUT-avdrag på företagsflytt?",
        answer:
          "RUT-avdrag gäller flytt av privatbostäder, inte kontorslokaler. Om er företagsflytt inkluderar att flytta en anställds bostad kan den delen ge rätt till RUT-avdrag med 50 % av arbetskostnaden. Renodlad kontorsflytt ger inte rätt till RUT-avdrag. Vi kan hjälpa er att dela upp fakturan om delar av flytten gäller en privatbostad.",
      },
      {
        question: "Hanterar ni IT-utrustning vid företagsflytt?",
        answer:
          "Ja, vi flyttar skärmar, datorer, skrivare, kopiatorer och servrar. Vi packar och skyddar all IT-utrustning för transport. Känslig utrustning som servrar bör kopplas loss av er IT-avdelning innan flytten – vi tar hand om allt det fysiska.",
      },
      {
        question: "Hur förbereder vi företaget inför flytten?",
        answer:
          "Utse en kontaktperson, låt medarbetare packa sina egna skrivbord i märkta kartonger, se till att IT-avdelningen kopplar loss och märker utrustning, rensa gemensamma ytor och boka hiss och lastzon vid båda adresserna.",
      },
      {
        question: "Kan ni flytta ett helt kontorsplan på en kväll?",
        answer:
          "Ja, vid större företagsflytt anpassar vi teamets storlek och arbetstid. Vi skalar upp med fler bärare och kan arbeta parallellt på flera plan. Många kontorsflytt genomförs under en enda kväll eller helg.",
      },
      {
        question: "Demonterar och monterar ni kontorsmöbler?",
        answer:
          "Ja, vi demonterar skrivbord, hyllsystem, arkivskåp och andra möbler som behöver tas isär. På de nya lokalerna monterar vi ihop allt igen och ställer det på den plats ni anvisar.",
      },
      {
        question: "Skyddar ni inventarierna under företagsflytten?",
        answer:
          "Ja, vi har möbelfiltar, bärremmar, skyddsmattor och spännband. Vi skyddar golv, väggar och dörrkarmar i både gamla och nya lokaler. Allt är försäkrat – om något skadas ersätter vi det.",
      },
      {
        question: "Kan ni flytta känsliga dokument och arkiv?",
        answer:
          "Ja, vi packar dokument och arkivmaterial separat i märkta kartonger och transporterar dem säkert. Om ni har sekretessmärkt material rekommenderar vi att ni själva packar det – vi ser till att kartongerna hanteras varsamt.",
      },
      {
        question: "Erbjuder ni företagsflytt i etapper?",
        answer:
          "Ja, vid stora kontorsflytt kan vi arbeta i etapper – avdelning för avdelning, våning för våning. Det gör att verksamheten aldrig behöver stanna helt och att medarbetare kan fortsätta jobba på de delar av kontoret som inte ännu har flyttats.",
      },
      {
        question: "Hur många bärare behövs för en företagsflytt?",
        answer:
          "Vi anpassar teamet efter kontorsytan och mängden inventarier. Ett kontor med 5–10 arbetsplatser behöver oftast två till tre bärare. Större kontor med 20+ arbetsplatser kan kräva fyra till sex personer. Beskriv er flytt vid bokning så rekommenderar vi rätt antal.",
      },
      {
        question: "Kan ni ta hand om kontorsmöbler som inte ska med?",
        answer:
          "Ja, inventarier som inte ska med till de nya lokalerna kan vi köra till återvinningsstation eller skänka till välgörenhet. Ni slipper ordna separat bortforsling – vi löser det i samma bokning.",
      },
      {
        question: "Erbjuder ni företagsflytt mellan Stockholm och Göteborg?",
        answer:
          "Ja, vi genomför företagsflytt både inom och mellan Stockholm och Göteborg. Priset beräknas utifrån avstånd och mängd inventarier. Ange adresserna vid bokning så ser ni kostnaden direkt.",
      },
      {
        question: "I vilka kommuner erbjuder ni företagsflytt?",
        answer:
          "Vi erbjuder företagsflytt i hela Stockholmsområdet: Botkyrka, Danderyd, Ekerö, Haninge, Huddinge, Järfälla, Lidingö, Nacka, Norrtälje, Nykvarn, Nynäshamn, Salem, Sigtuna, Sollentuna, Solna, Stockholm, Sundbyberg, Södertälje, Tyresö, Täby, Upplands Väsby, Upplands-Bro, Vallentuna, Vaxholm, Värmdö och Österåker. Vi finns även i Göteborgsområdet: Ale, Alingsås, Göteborg, Härryda, Kungälv, Kungsbacka, Lerum, Lilla Edet, Mölndal, Partille, Stenungsund, Tjörn och Öckerö.",
      },
    ],

    priceHint: PACKAGE_PRICE_HINT_SV.transport,

    pricingRows: getPackagePricingRows("transport", "sv"),

    dynamicHeadings: {
      threeSteps: "Företagsflytt i 3 enkla steg",
      step2Text:
        "Våra ungdomar packar, demonterar skrivbord och hyllsystem, bär ut allt, lastar skåpbilen och kör till era nya lokaler. Sedan bär vi in allt och ställer upp det på rätt plats.",
      step3Title: "Redo att jobba",
      step3Desc:
        "Vi ställer upp alla möbler, skrivbord och utrustning i de nya lokalerna. Ni kan börja arbeta direkt – allt står på plats.",
      ctaHeading: "Boka företagsflytt idag",
      ctaText:
        "Ange gamla och nya adressen, beskriv ert kontor och välj en tid. Vi erbjuder kvällar och helger. Ni ser priset direkt.",
    },

    en: {
      slug: "corporate-moving",
      meta: {
        title: "Corporate Moving – Smooth Office & Business Relocation | Ungdomskraft",
        description:
          "Book office moving in Stockholm & Gothenburg. We relocate desks, IT, archives and furniture – evenings and weekends available. Fixed price. Book today!",
        h1: "Corporate moving",
      },
      hero: {
        heading: "Professional corporate moving",
        intro:
          "Relocating your business? We move the entire operation — desks, office chairs, IT equipment, filing cabinets and everything else that needs to go to the new office. With a van and a team of strong, meticulous young workers, we carry out the move quickly and without disrupting your business. We offer corporate moving on evenings and weekends so your team can keep working as normal during the day.",
      },
      featureCards: {
        sinceText: "Corporate moving since 2014",
        bookYourText: "Book your corporate move",
        bookDescText:
          "Enter the old and new address, describe what needs moving and pick a time. We offer evenings and weekends. You see the price instantly.",
        vehicleTitleText: "Van sized for your office",
        vehicleDescText:
          "We arrive with the right vehicle for your business. From a single office room to an entire floor — we scale the van and team to your needs and make sure everything is transported safely.",
      },
      itemsSection: {
        heading: "Common items in a corporate move",
        items: [
          {
            name: "Desks & workstations",
            description: "Sit-stand desks, desk chairs and workstation equipment",
            image: "/office-desk.webp",
          },
          {
            name: "Office chairs",
            description: "Ergonomic office chairs, conference chairs and visitor chairs",
            image: "/armchair.webp",
          },
          {
            name: "Filing cabinets & shelves",
            description: "Document cabinets, archive shelves, bookcases and safes",
            image: "/wardrobe.webp",
          },
          {
            name: "IT equipment",
            description: "Monitors, computers, printers, copiers and servers",
            image: "/copy-machine.webp",
          },
          {
            name: "Conference rooms & furniture",
            description: "Conference tables, whiteboards, projectors and lounge furniture",
            image: "/table.webp",
          },
          {
            name: "Office kitchen & pantry",
            description: "Fridges, microwaves, coffee machines and kitchen equipment",
            image: "/mini-fridge.webp",
          },
        ],
      },
      seoContent: {
        mainHeading: "Corporate moving — your entire business relocated smoothly and without disruption",
        mainText:
          "A corporate move is about more than carrying furniture. It is about relocating an entire business — with minimal disruption to daily operations. With Ungdomskraft's corporate moving service you get a dedicated team that plans and carries out the whole move: we pack offices, disassemble desks and shelving systems, carry everything out, load the van, drive to the new premises and set everything up in the right place. IT equipment is handled with care, sensitive documents and archives are packed separately, and we make sure the new office is ready to work in when your team arrives on Monday morning. We offer corporate moving on evenings, weekends and during holiday periods — times that minimise disruption for you and your employees. Everything is included in one booking: vehicle, crew, protective materials and insurance. Whether you are moving a small office with five workstations or an entire office building across multiple floors, we scale the team and logistics to match your business needs.",
        preparationHeading: "Preparing for your corporate move",
        preparationText:
          "Inform all employees well in advance and appoint a contact person to coordinate with our team. Have each employee pack their own desk into labelled boxes — this saves time and reduces the risk of items getting mixed up. The IT department should disconnect and label all equipment: monitors, desktop computers, cables and peripherals. Clear shared spaces such as kitchens, storage and archives — anything that is not coming does not need to be moved. Book the lift and loading zone at both addresses and notify the building manager about the move. Make sure keys and access cards for the new premises are ready.",
        additionalSections: [
          {
            heading: "Small office and startup relocation",
            content:
              "Moving a smaller office with 5–15 workstations? You don't need a big removal company with three lorries. Our startup solution is built for fast, efficient office moves: a team of two to three people and a van that fits everything. We move desks, chairs, monitors, boxes and the small kitchen — often in a single evening or weekend day. The price is fixed and you see it when booking. Perfect for startups, consultancies and serviced offices.",
          },
          {
            heading: "Large office moves and multiple floors",
            content:
              "For a bigger corporate move — entire floors, several departments or a whole office building — we scale the team up. More carriers, larger vehicles and a detailed logistics plan that ensures everything ends up in the right place. We can work in parallel across multiple floors, label and catalogue inventory, and coordinate with your IT department to minimise system downtime. Heavy safes, server racks and specialist equipment are handled with the right techniques and gear. We often carry out large office moves in stages — department by department over several evenings — so that operations never stop completely.",
          },
          {
            heading: "What's included in corporate moving?",
            listItems: [
              {
                label: "Van",
                text: "We arrive with the right vehicle, sized for your office space and the volume of inventory.",
              },
              {
                label: "Packing & labelling",
                text: "We pack what isn't already in boxes and label everything by department and room.",
              },
              {
                label: "Disassembly",
                text: "Desks, shelving systems, filing cabinets and furniture that need taking apart before transport.",
              },
              {
                label: "Carrying & loading",
                text: "We carry all furniture, IT equipment and boxes out and load the van securely.",
              },
              {
                label: "Transport",
                text: "We drive the full distance to the new premises — whether it's in the same district or another city.",
              },
              {
                label: "Setup & assembly",
                text: "We unload, carry in, place furniture and equipment in the right spot and reassemble what was taken apart.",
              },
            ],
            afterContent:
              "You choose which parts you need. Just want transport and carrying? That works perfectly.",
          },
          {
            heading: "Why book corporate moving with Ungdomskraft?",
            content:
              "Ungdomskraft is not an ordinary removal company. We are a social enterprise that employs young people and gives them real work experience — and it shows in every corporate move we carry out. When you book corporate moving with us, you get a team of motivated, energetic young workers who genuinely want to deliver a professional result. Every assignment is a chance for them to prove themselves, and that mindset runs through the entire process — from how they handle your IT equipment to how they place the last chair in the new office.\n\nSince our founding in 2014, we have carried out hundreds of office and corporate moves across the Stockholm and Gothenburg regions. Along the way, we have given thousands of young people their first real work experience — with everything that entails: turning up on time, communicating professionally with clients and building managers, solving problems on-site, collaborating as a team and taking responsibility for the result. Our workers learn lifting techniques, safe loading, handling of sensitive equipment and how to carry out a move with minimal disruption to an ongoing business. They are trained to protect inventory, floors, walls and door frames — and to always leave both old and new premises in good condition.\n\nThis is not just good for the young people — it is good for you as a business. You notice the difference in commitment and energy. Our teams work in a structured and efficient way, double-check that everything is correctly packed and labelled, and always ask if there is anything else they can help with. We have built a culture where every corporate move is seen as an opportunity to exceed expectations, and it shows in the hundreds of five-star reviews we have received on Trustpilot and Google.\n\nBut it is not just about quality — it is about meaning. Every corporate move you book through Ungdomskraft directly contributes to a young person gaining work experience, learning a trade and building their CV. Many of our workers go on to permanent positions in logistics, transport or service industries — carrying with them the values and work ethic they learned with us. Choosing us means your corporate move becomes part of something bigger — and a tangible contribution to youth employment.\n\nWe also take sustainability seriously. Office furniture and equipment that are not coming to the new premises are sorted and taken to the right recycling station or donated to charity. Nothing is dumped unnecessarily. As a corporate client, you can include your collaboration with us in your CSR reporting as a concrete contribution to youth employment and the circular economy.\n\nIt is professional corporate moving with purpose.\n\nThe RUT tax deduction applies to moving private residences, not office premises. If a corporate move includes relocating an employee's home, that part may qualify for a 50% RUT deduction on the labour cost. We are happy to help split the invoice when needed.",
          },
        ],
      },
      faq: [
        {
          question: "What's included in corporate moving with Ungdomskraft?",
          answer:
            "Everything you need: a van, crew, packing, disassembly of desks and shelving, carrying, loading, transport to the new premises, unloading, carrying in, setup and assembly. You choose which parts you need.",
        },
        {
          question: "Can you carry out the corporate move on evenings or weekends?",
          answer:
            "Yes, we offer corporate moving on evenings, weekends and during holiday periods. It is the most common choice — most businesses want to minimise disruption and move when employees are not in the office.",
        },
        {
          question: "How much does corporate moving cost?",
          answer:
            "The price depends on the volume of inventory, distance and number of carriers. A small office with 5–10 workstations often comes to SEK 1,495–2,495. Larger office moves with 20+ workstations can come to SEK 2,495–4,995 or more. You see the price instantly when booking.",
        },
        {
          question: "Can we get a RUT tax deduction on corporate moving?",
          answer:
            "The RUT deduction applies to moving private residences, not office premises. If your corporate move includes relocating an employee's home, that part may qualify for a 50% RUT deduction on the labour cost. A purely office-based move does not qualify for RUT. We can help you split the invoice if parts of the move involve a private residence.",
        },
        {
          question: "Do you handle IT equipment during corporate moves?",
          answer:
            "Yes, we move monitors, computers, printers, copiers and servers. We pack and protect all IT equipment for transport. Sensitive equipment like servers should be disconnected by your IT department beforehand — we handle everything physical.",
        },
        {
          question: "How should we prepare the office for the move?",
          answer:
            "Appoint a contact person, have employees pack their own desks into labelled boxes, ensure IT disconnects and labels equipment, clear shared spaces and book the lift and loading zone at both addresses.",
        },
        {
          question: "Can you move an entire office floor in one evening?",
          answer:
            "Yes, for larger corporate moves we scale the team and working hours. We can work in parallel across multiple floors with extra carriers. Many office moves are completed in a single evening or weekend.",
        },
        {
          question: "Do you disassemble and reassemble office furniture?",
          answer:
            "Yes, we disassemble desks, shelving systems, filing cabinets and other furniture that needs taking apart. At the new premises we reassemble everything and place it where you direct.",
        },
        {
          question: "Do you protect office inventory during the move?",
          answer:
            "Yes, we bring furniture blankets, carrying straps, floor mats and ratchet straps. We protect floors, walls and door frames in both old and new premises. Everything is insured — if anything gets damaged, we compensate for it.",
        },
        {
          question: "Can you move sensitive documents and archives?",
          answer:
            "Yes, we pack documents and archive material separately in labelled boxes and transport them securely. For classified material we recommend you pack it yourselves — we make sure the boxes are handled with care.",
        },
        {
          question: "Do you offer corporate moving in stages?",
          answer:
            "Yes, for large office moves we can work in stages — department by department, floor by floor. This means operations never need to stop completely and employees can keep working in the parts of the office not yet moved.",
        },
        {
          question: "How many carriers are needed for a corporate move?",
          answer:
            "We scale the team to match your office and inventory. An office with 5–10 workstations usually needs two to three carriers. Larger offices with 20+ workstations may need four to six. Describe your move when booking and we'll recommend the right number.",
        },
        {
          question: "Can you take care of office furniture we no longer need?",
          answer:
            "Yes, inventory that is not coming to the new premises can be taken to a recycling station or donated to charity. You don't need to arrange separate removal — we handle it in the same booking.",
        },
        {
          question: "Do you offer corporate moving between Stockholm and Gothenburg?",
          answer:
            "Yes, we carry out corporate moves both within and between Stockholm and Gothenburg. The price is based on distance and volume. Enter both addresses when booking and you'll see the cost instantly.",
        },
        {
          question: "Which areas do you offer corporate moving in?",
          answer:
            "We offer corporate moving throughout the Stockholm region: Botkyrka, Danderyd, Ekerö, Haninge, Huddinge, Järfälla, Lidingö, Nacka, Norrtälje, Nykvarn, Nynäshamn, Salem, Sigtuna, Sollentuna, Solna, Stockholm, Sundbyberg, Södertälje, Tyresö, Täby, Upplands Väsby, Upplands-Bro, Vallentuna, Vaxholm, Värmdö and Österåker. We also operate in the Gothenburg area: Ale, Alingsås, Göteborg, Härryda, Kungälv, Kungsbacka, Lerum, Lilla Edet, Mölndal, Partille, Stenungsund, Tjörn and Öckerö.",
        },
      ],
      priceHint: PACKAGE_PRICE_HINT_EN.transport,
      pricingRows: getPackagePricingRows("transport", "en"),
      dynamicHeadings: {
        threeSteps: "Corporate moving in 3 easy steps",
        step2Text:
          "Our workers pack, disassemble desks and shelving, carry everything out, load the van and drive to your new premises. Then we carry everything in and set it up in the right place.",
        step3Title: "Ready to work",
        step3Desc:
          "We set up all furniture, desks and equipment in the new premises. Your team can start working straight away — everything is in place.",
        ctaHeading: "Book corporate moving today",
        ctaText:
          "Enter the old and new address, describe your office and pick a time. We offer evenings and weekends. You see the price instantly.",
      },
    },
  },

  {
    // Studentflytt — config för /studentflytt
    // Vinkel: Flytt för studenter — till och från universitetsstäder, korridorsrum, ettor, studentlägenheter.
    // Unik vinkel vs flytthjälp: budgetanpassat för studenter, mindre bohag, terminsstart/terminslut-fokus.
    // Unik vinkel vs bohagsflytt: inte ett helt hushåll, utan studentens begränsade ägodelar.
    slug: "studentflytt",
    serviceCategory: "flytt",

    meta: {
      title: "Studentflytt – Billig & Smidig Flytt för Studenter | Ungdomskraft",
      description:
        "Boka studentflytt med skåpbil och personal. Vi flyttar dig till eller från studentbostaden – snabbt, prisvärt och utan stress. Perfekt vid terminsstart eller examen.",
      h1: "Studentflytt",
    },

    hero: {
      heading: "Smidig och prisvärd studentflytt",
      intro:
        "Ska du flytta till en ny stad för att plugga, byta korridor eller flytta hem efter examen? Vår studentflytt är anpassad för dig med mindre bohag och tight budget. Du får ett team av unga, starka bärare och en skåpbil som rymmer allt du äger – säng, skrivbord, kartonger och cykel. Vi packar, bär, kör och lossar. Du behöver bara visa var sakerna ska stå.",
      heroImage: "/hero-studentflytt.png",
    },

    featureCards: {
      sinceText: "Studentflytt sedan 2014",
      storyText:
        "Sedan 2014 har Ungdomskraft hjälpt tusentals studenter att flytta till och från universitetsstäder. Våra bärare är själva unga – de förstår studentlivet och vet att varje krona räknas.",
      bookYourText: "Boka din studentflytt",
      bookDescText:
        "Ange gamla och nya adressen, beskriv vad du har och välj en tid. Du ser priset direkt – inga dolda kostnader.",
      vehicleTitleText: "Skåpbil perfekt för en studentflytt",
      vehicleDescText:
        "Du behöver inte hyra bil eller be föräldrar köra. Vi kommer med en skåpbil som rymmer en hel studentetta – säng, skrivbord, bokhylla och alla kartonger. Snabbt, smidigt och till studentvänligt pris.",
    },

    itemsSection: {
      heading: "Vanligt vi flyttar åt studenter",
      items: [
        {
          name: "Säng & madrass",
          description: "Enkelsäng, madrass eller bäddsoffa – vi bär genom trånga trapphus",
          image: "/bed.webp",
        },
        {
          name: "Skrivbord & stol",
          description: "Skrivbord, kontorsstol och bokhylla – demonteras vid behov",
          image: "/office-desk.webp",
        },
        {
          name: "Flyttkartonger",
          description: "Böcker, kläder, köksprylar och personliga saker i kartonger",
          image: "/box.webp",
        },
        {
          name: "Cykel",
          description: "Din cykel åker med i skåpbilen – ingen extra kostnad",
          image: "/patio-furniture.webp",
        },
        {
          name: "TV & elektronik",
          description: "TV, skärm, dator och spelkonsol – vi packar och skyddar",
          image: "/tv.webp",
        },
        {
          name: "Soffa eller fåtölj",
          description: "Liten soffa, fåtölj eller saccosäck som gör studentrummet komplett",
          image: "/sofa.webp",
        },
      ],
    },

    seoContent: {
      mainHeading: "Studentflytt – snabb, billig och stressfri flytt för studenter",
      mainText:
        "Att flytta som student ska inte behöva vara dyrt eller krångligt. Med Ungdomskrafts studentflytt får du ett team av unga bärare och en skåpbil som kör hela sträckan – oavsett om du flyttar till en korridor i Uppsala, en etta i Lund eller hem till föräldrarna efter examen. Vi packar det som inte redan sitter i kartonger, bär ut allt genom trånga korridorer och trapphus, lastar skåpbilen och kör till din nya adress. Där bär vi in allt och ställer det på plats. Du slipper ringa runt efter kompisar med bil, hyra släpvagn eller ta ledigt från tentan för att organisera flytten. Allt ingår i en bokning: fordon, personal och försäkring. Priset är fast och du ser det redan när du bokar – perfekt för en studentbudget. De flesta studentflytt tar bara ett par timmar, så du har tid att plugga, jobba eller bara landa i din nya stad.",
      preparationHeading: "Förbered inför din studentflytt",
      preparationText:
        "Packa det du kan i kartonger eller resväskor – det sparar tid och pengar. Märk kartongerna med innehåll så att det går snabbt att packa upp. Demontera skrivbord och bokhyllor om de inte får plats genom dörren hela. Samla skruvar i en påse och tejpa den på möbeln. Kolla att det finns parkeringsplats för skåpbilen vid båda adresserna och att du har nycklar eller portkod redo. Bor du i korridor? Meddela dina grannar att det blir lite spring i hallen.",
      additionalSections: [
        {
          heading: "Flytta till universitetsstad",
          content:
            "Terminsstart innebär att tusentals studenter ska flytta till nya städer samtidigt. Konkurrensen om hyrbilar och släpvagnar är stenhård, och att be kompisar hjälpa till mitt i inflyttningsruschen fungerar sällan. Med vår studentflytt slipper du stressen. Vi bokar in din flytt på en tid som passar dig, kör hela sträckan och bär in allt i ditt nya rum. Oavsett om du flyttar till Stockholm, Göteborg, Uppsala, Lund, Linköping eller en annan studieort hämtar vi dig där du bor och kör dit du ska. Perfekt när du flyttar hemifrån för första gången eller byter stad mellan kandidat och master.",
        },
        {
          heading: "Flytta från universitetsstad",
          content:
            "Examen klar, kontrakt upphör eller praktikplats i en annan stad? Att flytta från en studentbostad går ofta snabbt – kontraktet sägs upp med kort varsel och plötsligt ska allt vara ute. Vi fixar studentflytten på kort varsel, ofta redan samma vecka. Vi packar, bär ut ur korridorsrummet eller ettan, lastar skåpbilen och kör till din nya adress – oavsett om det är hem till föräldrarna, en ny lägenhet eller en kompis soffa medan du letar bostad.",
        },
        {
          heading: "Vad ingår i en studentflytt?",
          listItems: [
            {
              label: "Skåpbil",
              text: "Vi kommer med en skåpbil som rymmer en hel studentetta – du behöver inte hyra bil eller be någon köra.",
            },
            {
              label: "Bärning",
              text: "Vi bär ut allt genom korridorer, trappor och trånga dörrar – och bär in det på nya adressen.",
            },
            {
              label: "Demontering",
              text: "Skrivbord, bokhyllor och sängramar som behöver tas isär – vi monterar ihop dem igen på plats.",
            },
            {
              label: "Transport",
              text: "Vi kör hela sträckan, oavsett om det är inom staden eller mellan städer.",
            },
            {
              label: "Packning",
              text: "Har du inte hunnit packa allt? Vi packar det som återstår åt dig.",
            },
            {
              label: "Försäkring",
              text: "Allt är försäkrat under transporten – om något skadas ersätter vi det.",
            },
          ],
          afterContent:
            "Du väljer vilka delar du behöver. Har du redan packat och bara behöver bärning och transport? Perfekt – boka bara det.",
        },
        {
          heading: "Varför boka studentflytt med Ungdomskraft?",
          content:
            "Ungdomskraft är inte en vanlig flyttfirma – och det passar studenter extra bra. Vi är ett socialt företag som sysselsätter ungdomar och ger dem riktig arbetslivserfarenhet. Det innebär att teamet som bär dina grejer förstår din situation på riktigt. De är själva unga, de vet vad det innebär att ha tight budget och de behandlar dina saker som sina egna. Varje studentflytt är för dem en chans att visa vad de går för, och den inställningen märks i allt – från hur de packar din dator till hur de bär din säng uppför fem trappor utan hiss.\n\nSedan starten 2014 har vi genomfört tusentals studentflytt i Stockholms- och Göteborgsområdet och till universitetsstäder runt om i Sverige. Under den resan har vi gett tusentals ungdomar deras första riktiga arbetslivserfarenhet – med allt vad det innebär: att komma i tid, kommunicera professionellt, lösa problem på plats, samarbeta i team och ta ansvar för resultatet. Våra medarbetare lär sig packningsteknik, lyftteknik, säker lastning och hur man hanterar allt från bräckliga skärmar till tunga bokhyllor. De tränas i att skydda dina ägodelar, golv, väggar och dörrkarmar – och i att alltid lämna både gamla och nya bostaden i gott skick.\n\nDet här är inte bara bra för ungdomarna – det är bra för dig som kund. Du märker skillnaden i engagemang och energi. Våra team springer där andra går, dubbelkollar att allt är rätt lastat och frågar alltid om det finns något mer de kan hjälpa till med. Vi har byggt en kultur där varje studentflytt ses som en chans att överträffa förväntningarna, och det syns i de hundratals femstjärniga recensioner vi fått på Trustpilot och Google.\n\nMen det handlar inte bara om kvalitet – det handlar om mening. Varje studentflytt du bokar genom Ungdomskraft bidrar direkt till att en ung person får arbetslivserfarenhet, lär sig ett yrke och bygger sitt cv. Många av våra medarbetare går vidare till fasta anställningar inom logistik, transport eller serviceyrken – och de bär med sig de värderingar och den arbetsmoral de lärt sig hos oss. Att välja oss innebär att din studentflytt blir en del av något större.\n\nVi tar också hållbarhet på allvar. Möbler och prylar som inte ska med sorterar vi och kör till rätt återvinningsstation eller skänker till välgörenhetsorganisationer som Myrorna, Erikshjälpen och Stadsmissionen. Ingenting slängs i onödan.\n\nDet är prisvärd studentflytt med mening.\n\nStudentflytt ger rätt till RUT-avdrag. Du får avdrag på 50 % av arbetskostnaden – upp till 75 000 kr per person och år. Avdraget hanteras direkt på fakturan, vilket gör din studentflytt ännu billigare.",
        },
      ],
    },

    faq: [
      {
        question: "Hur mycket kostar en studentflytt?",
        answer:
          "De flesta studentflytt landar på 595–1 495 kr beroende på mängd, avstånd och antal bärare. En etta eller ett korridorsrum med säng, skrivbord och kartonger hamnar ofta i Extra Liten eller Small. Du ser priset direkt vid bokning – inga dolda kostnader.",
      },
      {
        question: "Kan jag få RUT-avdrag på studentflytt?",
        answer:
          "Självklart, studentflytt ger rätt till RUT-avdrag. Du får avdrag på 50 % av arbetskostnaden, upp till 75 000 kr per person och år. Avdraget gäller flytt av din bostad – packning, bärning och transport. Vi hanterar RUT-avdraget direkt på fakturan, så du betalar bara halva arbetskostnaden. Perfekt för studentbudgeten.",
      },
      {
        question: "Kan ni studentflytta med kort varsel?",
        answer:
          "Ja, vi erbjuder ofta studentflytt med kort varsel – ibland redan samma vecka. Boka online så ser du tillgängliga tider direkt. Perfekt när kontraktet sägs upp plötsligt eller terminen börjar snart.",
      },
      {
        question: "Ingår skåpbil i studentflytten?",
        answer:
          "Ja, skåpbil ingår alltid. Vi kommer med rätt storlek – du behöver inte hyra bil, köra eller tanka. Allt sköts av oss.",
      },
      {
        question: "Kan ni flytta mig till en annan stad för studier?",
        answer:
          "Ja, vi utför studentflytt både inom och mellan städer. Oavsett om du flyttar från Stockholm till Uppsala, Göteborg till Lund eller hem till föräldrarna beräknas priset utifrån avstånd och mängd. Du ser kostnaden direkt vid bokning.",
      },
      {
        question: "Hur lång tid tar en studentflytt?",
        answer:
          "De flesta studentflytt tar mellan en och tre timmar beroende på mängd bohag och avstånd. En etta med säng, skrivbord och kartonger går ofta på under två timmar.",
      },
      {
        question: "Behöver jag packa själv innan studentflytten?",
        answer:
          "Ju mer du packar själv, desto snabbare och billigare blir det. Men har du inte hunnit packa allt kan vi hjälpa till – vi packar det som återstår och har skyddsmaterial med oss för ömtåliga saker.",
      },
      {
        question: "Kan ni bära genom trånga studentkorridorer?",
        answer:
          "Ja, vi är vana vid korridorsboenden, smala trapphus, tunga branddörrar och byggnader utan hiss. Våra bärare vet hur man manövrerar möbler genom trånga utrymmen utan att skada väggar eller dörrkarmar.",
      },
      {
        question: "Kan jag ta med cykeln i skåpbilen?",
        answer:
          "Ja, din cykel åker med i skåpbilen utan extra kostnad. Vi lastar den säkert så att den inte skadas under transporten.",
      },
      {
        question: "Demonterar och monterar ni möbler vid studentflytt?",
        answer:
          "Ja, vi demonterar skrivbord, bokhyllor och sängramar som behöver tas isär. På nya adressen monterar vi ihop dem igen. Spara gärna skruvar i en märkt påse.",
      },
      {
        question: "Skyddar ni mina saker under studentflytten?",
        answer:
          "Ja, vi har möbelfiltar, bärremmar och spännband. Vi skyddar golv och väggar vid bärning. Allt är försäkrat – om något skadas ersätter vi det.",
      },
      {
        question: "Kan jag boka studentflytt på helger?",
        answer:
          "Ja, vi erbjuder studentflytt på helger och vissa kvällar. Många studenter föredrar att flytta på en lördag. Boka online och se alla tillgängliga tider.",
      },
      {
        question: "Är studentflytt billigare än vanlig flytthjälp?",
        answer:
          "Studentflytt innebär oftast mindre bohag och kortare tid, vilket gör att det naturligt blir billigare. Våra Extra Liten och Small-paket är perfekta för studenter – från 595 kr.",
      },
      {
        question: "Kan ni studentflytta vid terminsstart och terminslut?",
        answer:
          "Ja, vi har extra kapacitet vid terminsstart och terminslut då många studenter flyttar samtidigt. Boka i god tid – de populäraste tiderna fylls snabbt i augusti och januari.",
      },
      {
        question: "I vilka kommuner erbjuder ni studentflytt?",
        answer:
          "Vi erbjuder studentflytt i hela Stockholmsområdet: Botkyrka, Danderyd, Ekerö, Haninge, Huddinge, Järfälla, Lidingö, Nacka, Norrtälje, Nykvarn, Nynäshamn, Salem, Sigtuna, Sollentuna, Solna, Stockholm, Sundbyberg, Södertälje, Tyresö, Täby, Upplands Väsby, Upplands-Bro, Vallentuna, Vaxholm, Värmdö och Österåker. Vi finns även i Göteborgsområdet: Ale, Alingsås, Göteborg, Härryda, Kungälv, Kungsbacka, Lerum, Lilla Edet, Mölndal, Partille, Stenungsund, Tjörn och Öckerö. Vi kör även till universitetsstäder som Uppsala, Lund, Linköping och fler.",
      },
    ],

    priceHint: PACKAGE_PRICE_HINT_SV.transport,

    pricingRows: getPackagePricingRows("transport", "sv"),

    dynamicHeadings: {
      threeSteps: "Studentflytt i 3 enkla steg",
      step2Text:
        "Våra ungdomar packar, bär ut allt ur studentlägenheten, lastar skåpbilen och kör till din nya adress. Sedan bär vi in allt och ställer det på plats. Du behöver inte göra något.",
      step3Title: "Allt på plats",
      step3Desc:
        "Vi bär in allt i ditt nya rum och ställer möbler och kartonger där du vill ha dem. Du kan börja plugga, landa och trivas direkt.",
      ctaHeading: "Boka studentflytt idag",
      ctaText:
        "Ange gamla och nya adressen, beskriv vad du har och välj en tid. Du ser priset direkt – skåpbil och personal ingår.",
    },

    en: {
      slug: "student-moving",
      meta: {
        title: "Student Moving – Cheap & Easy Moves for Students | Ungdomskraft",
        description:
          "Book student moving with van and crew. We move you to or from your student flat — fast, affordable and stress-free. Perfect for term start, flat swaps or graduation.",
        h1: "Student moving",
      },
      hero: {
        heading: "Easy and affordable student moving",
        intro:
          "Moving to a new city for university, swapping dorms or heading home after graduation? Our student moving service is built for smaller households and tight budgets. You get a team of young, strong carriers and a van that fits everything you own — bed, desk, boxes and bike. We pack, carry, drive and unload. You just point to where things go.",
      },
      featureCards: {
        sinceText: "Student moving since 2014",
        bookYourText: "Book your student move",
        bookDescText:
          "Enter the old and new address, describe what you have and pick a time. You see the price instantly — no hidden fees.",
        vehicleTitleText: "Van perfect for a student move",
        vehicleDescText:
          "No need to rent a car or ask your parents to drive. We arrive with a van that fits an entire student flat — bed, desk, bookshelf and all the boxes. Fast, easy and at a student-friendly price.",
      },
      itemsSection: {
        heading: "Common items we move for students",
        items: [
          {
            name: "Bed & mattress",
            description: "Single bed, mattress or sofa bed — carried through narrow stairwells",
            image: "/bed.webp",
          },
          {
            name: "Desk & chair",
            description: "Desk, office chair and bookshelf — disassembled if needed",
            image: "/office-desk.webp",
          },
          {
            name: "Moving boxes",
            description: "Books, clothes, kitchen stuff and personal items in boxes",
            image: "/box.webp",
          },
          {
            name: "Bicycle",
            description: "Your bike rides along in the van — no extra charge",
            image: "/patio-furniture.webp",
          },
          {
            name: "TV & electronics",
            description: "TV, monitor, computer and games console — packed and protected",
            image: "/tv.webp",
          },
          {
            name: "Sofa or armchair",
            description: "Small sofa, armchair or bean bag that completes the student room",
            image: "/sofa.webp",
          },
        ],
      },
      seoContent: {
        mainHeading: "Student moving — fast, affordable and stress-free moves for students",
        mainText:
          "Moving as a student shouldn't be expensive or complicated. With Ungdomskraft's student moving service you get a team of young carriers and a van that drives the whole way — whether you're moving to a student corridor in Uppsala, a studio in Lund or back to your parents' place after graduation. We pack what isn't already in boxes, carry everything out through narrow corridors and stairwells, load the van and drive to your new address. There we carry everything in and put it in place. You don't need to call around for friends with cars, rent a trailer or skip an exam to organise the move. Everything is included in one booking: vehicle, crew and insurance. The price is fixed and you see it when booking — perfect for a student budget. Most student moves take just a couple of hours, so you have time to study, work or just settle into your new city.",
        preparationHeading: "Preparing for your student move",
        preparationText:
          "Pack what you can into boxes or suitcases — it saves time and money. Label the boxes so unpacking goes quickly. Disassemble desks and bookshelves if they won't fit through the door whole. Collect screws in a bag and tape it to the furniture. Check that there's parking for the van at both addresses and have keys or door codes ready. Living in a corridor? Let your neighbours know there'll be some activity in the hallway.",
        additionalSections: [
          {
            heading: "Moving to a university city",
            content:
              "Term start means thousands of students moving to new cities at the same time. Competition for rental vans and trailers is fierce, and asking friends to help in the middle of the move-in rush rarely works out. With our student moving service you skip the stress. We book your move at a time that works for you, drive the full distance and carry everything into your new room. Whether you're moving to Stockholm, Gothenburg, Uppsala, Lund, Linköping or another university town, we pick you up where you live and drive where you need to go. Perfect when leaving home for the first time or switching cities between bachelor's and master's.",
          },
          {
            heading: "Moving from a university city",
            content:
              "Degree done, lease ending or internship in another city? Moving out of student housing often happens fast — the lease is terminated at short notice and suddenly everything needs to be out. We handle student moves at short notice, often the same week. We pack, carry out of the corridor room or studio, load the van and drive to your new address — whether it's your parents' house, a new flat or a friend's sofa while you look for somewhere to live.",
          },
          {
            heading: "What's included in student moving?",
            listItems: [
              {
                label: "Van",
                text: "We arrive with a van that fits an entire student flat — no need to rent a car or ask someone to drive.",
              },
              {
                label: "Carrying",
                text: "We carry everything out through corridors, stairs and tight doorways — and carry it in at the new address.",
              },
              {
                label: "Disassembly",
                text: "Desks, bookshelves and bed frames that need taking apart — we reassemble them at the new place.",
              },
              {
                label: "Transport",
                text: "We drive the full distance, whether it's within the city or between cities.",
              },
              {
                label: "Packing",
                text: "Haven't had time to pack everything? We pack what's left and bring protective materials for fragile items.",
              },
              {
                label: "Insurance",
                text: "Everything is insured during transport — if anything gets damaged, we compensate for it.",
              },
            ],
            afterContent:
              "You choose which parts you need. Already packed and just need carrying and transport? Perfect — book exactly that.",
          },
          {
            heading: "Why book student moving with Ungdomskraft?",
            content:
              "Ungdomskraft is not an ordinary removal company — and that suits students especially well. We are a social enterprise that employs young people and gives them real work experience. The team carrying your stuff genuinely understands your situation. They are young themselves, they know what a tight budget means and they treat your belongings as their own. Every student move is a chance for them to prove themselves, and that mindset shows in everything — from how they pack your laptop to how they carry your bed up five flights of stairs with no lift.\n\nSince our founding in 2014, we have carried out thousands of student moves across the Stockholm and Gothenburg regions and to university cities around Sweden. Along the way, we have given thousands of young people their first real work experience — with everything that entails: turning up on time, communicating professionally, solving problems on-site, collaborating as a team and taking responsibility for the result. Our workers learn packing techniques, lifting techniques, safe loading and how to handle everything from fragile screens to heavy bookshelves. They are trained to protect your belongings, floors, walls and door frames — and to always leave both old and new homes in good condition.\n\nThis is not just good for the young people — it is good for you as a customer. You notice the difference in commitment and energy. Our teams run where others walk, double-check that everything is loaded correctly and always ask if there is anything else they can help with. We have built a culture where every student move is seen as a chance to exceed expectations, and it shows in the hundreds of five-star reviews we have received on Trustpilot and Google.\n\nBut it is not just about quality — it is about meaning. Every student move you book through Ungdomskraft directly contributes to a young person gaining work experience, learning a trade and building their CV. Many of our workers go on to permanent positions in logistics, transport or service industries — carrying with them the values and work ethic they learned with us. Choosing us means your student move becomes part of something bigger.\n\nWe also take sustainability seriously. Furniture and items that are not coming with you are sorted and taken to the right recycling station or donated to charities such as Myrorna, Erikshjälpen and Stadsmissionen. Nothing is dumped unnecessarily.\n\nIt is affordable student moving with purpose.\n\nStudent moving qualifies for the Swedish RUT tax deduction. You get a 50% deduction on the labour cost – up to SEK 75,000 per person per year. The deduction is handled directly on the invoice, making your student move even more affordable.",
          },
        ],
      },
      faq: [
        {
          question: "How much does student moving cost?",
          answer:
            "Most student moves come to SEK 595–1,495 depending on volume, distance and number of carriers. A corridor room or studio with a bed, desk and boxes usually fits Extra Liten or Small. You see the price instantly when booking — no hidden fees.",
        },
        {
          question: "Can I get a RUT tax deduction on student moving?",
          answer:
            "Of course, student moving qualifies for the Swedish RUT tax deduction. You get a 50% deduction on the labour cost, up to SEK 75,000 per person per year. The deduction covers moving your home – packing, carrying and transport. We handle the RUT deduction directly on the invoice, so you only pay half the labour cost. Great for the student budget.",
        },
        {
          question: "Can you do a student move at short notice?",
          answer:
            "Yes, we often offer student moving at short notice — sometimes the same week. Book online and you'll see available times straight away. Perfect when your lease ends suddenly or term starts soon.",
        },
        {
          question: "Is a van included in the student move?",
          answer:
            "Yes, a van is always included. We arrive with the right size — you don't need to rent a car, drive or refuel. Everything is handled by us.",
        },
        {
          question: "Can you move me to another city for university?",
          answer:
            "Yes, we carry out student moves both within and between cities. Whether you're moving from Stockholm to Uppsala, Gothenburg to Lund or back to your parents' place, the price is based on distance and volume. You see the cost instantly when booking.",
        },
        {
          question: "How long does a student move take?",
          answer:
            "Most student moves take one to three hours depending on the volume and distance. A studio with a bed, desk and boxes is often done in under two hours.",
        },
        {
          question: "Do I need to pack everything myself before the student move?",
          answer:
            "The more you pack yourself, the faster and cheaper it is. But if you haven't had time to pack everything, we can help — we pack what's left and bring protective materials for fragile items.",
        },
        {
          question: "Can you carry things through narrow student corridors?",
          answer:
            "Yes, we're used to corridor housing, narrow stairwells, heavy fire doors and buildings with no lift. Our carriers know how to manoeuvre furniture through tight spaces without damaging walls or door frames.",
        },
        {
          question: "Can I bring my bike in the van?",
          answer:
            "Yes, your bike rides along in the van at no extra charge. We load it securely so it's not damaged during transport.",
        },
        {
          question: "Do you disassemble and reassemble furniture for student moves?",
          answer:
            "Yes, we disassemble desks, bookshelves and bed frames that need taking apart. At the new address we reassemble them. Keep screws in a labelled bag for a smoother process.",
        },
        {
          question: "Do you protect my things during the student move?",
          answer:
            "Yes, we bring furniture blankets, carrying straps and ratchet straps. We protect floors and walls when carrying. Everything is insured — if anything gets damaged, we compensate for it.",
        },
        {
          question: "Can I book a student move on weekends?",
          answer:
            "Yes, we offer student moving on weekends and some evenings. Many students prefer Saturday. Book online and you'll see all available slots.",
        },
        {
          question: "Is student moving cheaper than regular moving help?",
          answer:
            "Student moves usually involve less stuff and take less time, which naturally makes them cheaper. Our Extra Liten and Small packages are perfect for students — from SEK 595.",
        },
        {
          question: "Can you handle student moves during term start and term end?",
          answer:
            "Yes, we have extra capacity during term start and term end when many students move at the same time. Book in advance — the most popular slots fill up quickly in August and January.",
        },
        {
          question: "Which areas do you offer student moving in?",
          answer:
            "We offer student moving throughout the Stockholm region: Botkyrka, Danderyd, Ekerö, Haninge, Huddinge, Järfälla, Lidingö, Nacka, Norrtälje, Nykvarn, Nynäshamn, Salem, Sigtuna, Sollentuna, Solna, Stockholm, Sundbyberg, Södertälje, Tyresö, Täby, Upplands Väsby, Upplands-Bro, Vallentuna, Vaxholm, Värmdö and Österåker. We also operate in the Gothenburg area: Ale, Alingsås, Göteborg, Härryda, Kungälv, Kungsbacka, Lerum, Lilla Edet, Mölndal, Partille, Stenungsund, Tjörn and Öckerö. We also drive to university cities like Uppsala, Lund, Linköping and more.",
        },
      ],
      priceHint: PACKAGE_PRICE_HINT_EN.transport,
      pricingRows: getPackagePricingRows("transport", "en"),
      dynamicHeadings: {
        threeSteps: "Student moving in 3 easy steps",
        step2Text:
          "Our workers pack, carry everything out of the student flat, load the van and drive to your new address. Then we carry everything in and put it in place. You don't need to do a thing.",
        step3Title: "Everything in place",
        step3Desc:
          "We carry everything into your new room and place furniture and boxes where you want them. You can start studying, settling in and enjoying your new city straight away.",
        ctaHeading: "Book student moving today",
        ctaText:
          "Enter the old and new address, describe what you have and pick a time. You see the price instantly — van and crew included.",
      },
    },
  },

  {
    // Flytta möbler — config för /flytta-mobler
    // Vinkel: Flytta enskilda möbler eller en möbelgrupp från en adress till en annan.
    // Unik vinkel vs bortforsling-möbler: här FLYTTAS möblerna till ny adress (behålls), där FORSLAS de bort (slängs/återvinns).
    // Unik vinkel vs bohagsflytt/flytthjälp: inte en helhetsflytt av ett hem, utan specifika möbler.
    // Unik vinkel vs bärhjälp: vi kör möblerna med skåpbil, inte bara bär på plats.
    slug: "flytta-mobler",
    serviceCategory: "flytt",

    meta: {
      title: "Flytta Möbler – Transport & Bärning av Möbler | Ungdomskraft",
      description:
        "Boka hjälp att flytta möbler med skåpbil och personal. Vi hämtar, bär och levererar möblerna på plats. Perfekt vid köp, flytt eller renovering. Fast pris.",
      h1: "Möbelflytt",
    },

    hero: {
      heading: "Hjälp att flytta möbler",
      intro:
        "Ska du flytta en soffa, ett matbord eller en hel möbelgrupp till en ny adress? Vi hämtar möblerna där de står, bär ut dem, kör med skåpbil och bär in dem på den nya platsen. Perfekt vid Blocket-köp, omflyttning mellan rum, renovering eller när du ska möblera en ny bostad. Du behöver inte hyra bil, bära själv eller ringa runt efter hjälp.",
      heroImage: "/hero-flytta-mobler.png",
    },

    featureCards: {
      sinceText: "Möbelflytt sedan 2014",
      storyText:
        "Sedan 2014 har Ungdomskraft flyttat tusentals möbler – soffor, sängar, matgrupper, garderober och allt däremellan. Våra ungdomar är tränade i lyftteknik och vet hur man skyddar både möbler och fastighet.",
      bookYourText: "Boka din möbelflytt",
      bookDescText:
        "Ange hämtadress och leveransadress, beskriv vilka möbler som ska flyttas och välj en tid. Du ser priset direkt.",
      vehicleTitleText: "Skåpbil anpassad för dina möbler",
      vehicleDescText:
        "Vi kommer med rätt fordon för dina möbler. En enda fåtölj eller en komplett matgrupp – vi dimensionerar skåpbilen efter det som ska flyttas och ser till att allt transporteras säkert.",
    },

    itemsSection: {
      heading: "Vanliga möbler vi flyttar",
      items: [
        {
          name: "Soffor & hörnsoffor",
          description: "Bäddsoffor, hörnsoffor och tunga skinnsoffor – vi skyddar och bär",
          image: "/sofa.webp",
        },
        {
          name: "Sängar & madrasser",
          description: "Dubbelsängar, continental-sängar och resårmadrasser",
          image: "/bed.webp",
        },
        {
          name: "Matbord & stolar",
          description: "Matgrupper, soffbord, skrivbord och stolar",
          image: "/table.webp",
        },
        {
          name: "Garderober & byråer",
          description: "PAX-garderober, linneskåp, byråer och bokhyllor – demonteras vid behov",
          image: "/wardrobe.webp",
        },
        {
          name: "Fåtöljer & sittmöbler",
          description: "Öronlappsfåtöljer, kontorsstolar och fotöljer",
          image: "/armchair.webp",
        },
        {
          name: "Trädgårdsmöbler",
          description: "Utemöbelgrupper, parasoll och loungemöbler",
          image: "/patio-furniture.webp",
        },
      ],
    },

    seoContent: {
      mainHeading: "Flytta möbler – hämtning, transport och inbärning i ett paket",
      mainText:
        "Ibland behöver du inte flytta ett helt hem – bara en soffa som ska till den nya lägenheten, ett matbord du köpt på Blocket eller en garderob som ska från föräldrarna till din bostad. Med Ungdomskrafts möbelflytt får du ett team som hämtar möblerna där de står, bär ut dem genom trappor och trånga dörrar, lastar skåpbilen och kör till den nya adressen. Där bär vi in allt och ställer möblerna exakt där du vill ha dem. Vi demonterar det som behöver tas isär och monterar ihop det igen på plats. Allt ingår i en bokning: fordon, personal, skyddsmaterial och försäkring. Du behöver inte hyra bil, köra själv eller bära något tungt. Oavsett om det gäller en enda möbel eller en hel möbelgrupp anpassar vi teamet och fordonet efter uppdraget. Många bokar oss vid Blocket-köp och försäljning, vid renovering, omflyttning eller när de möblerar en ny bostad.",
      preparationHeading: "Förbered inför möbelflytten",
      preparationText:
        "Mät dina möbler och kontrollera att de passar genom dörrar och trapphus på båda adresserna. Stora möbler som garderober, sängramar och matbord går ofta snabbare att flytta i delar – demontera dem i förväg om du kan, eller låt oss göra det. Samla skruvar och beslag i märkta påsar. Töm lådor och hyllor innan vi kommer – det gör möblerna lättare och minskar risken att saker ramlar ut. Se till att det finns fri passage och att skåpbilen kan parkera nära porten vid båda adresserna.",
      additionalSections: [
        {
          heading: "Flytta möbler vid köp och försäljning",
          content:
            "Köpt en soffa på Blocket, Facebook Marketplace eller en auktion? Eller har du sålt möbler och behöver leverera dem till köparen? Vi hämtar möblerna på säljarens adress, bär ut, lastar, kör och bär in dem hos köparen. Du slipper hyra bil, be om hjälp och riskera att skada möblerna eller din bil. Priset är fast och du ser det vid bokning – perfekt oavsett om det är en enda fåtölj eller en hel matgrupp.",
        },
        {
          heading: "Flytta möbler vid renovering och omflyttning",
          content:
            "Ska du renovera och behöver flytta möbler till ett annat rum, ett förråd eller en tillfällig adress? Vi bär ut möblerna, kör dem dit de ska och bär in dem igen. När renoveringen är klar kan vi flytta tillbaka allt. Vi hanterar även möbler som ska byta plats inom samma bostad – mellan våningar, rum eller från lägenhet till källarförråd. Perfekt vid golvslipning, målning eller köksrenovering.",
        },
        {
          heading: "Vilka möbler kan ni flytta?",
          listItems: [
            {
              label: "Soffor",
              text: "Bäddsoffor, hörnsoffor, skinnsoffor och soffgrupper – vi skyddar och bär.",
            },
            {
              label: "Sängar",
              text: "Enkelsängar, dubbelsängar, continental-sängar och sängramar – demonteras vid behov.",
            },
            {
              label: "Bord & stolar",
              text: "Matbord, matgrupper, skrivbord, soffbord och stolar av alla slag.",
            },
            {
              label: "Förvaring",
              text: "Garderober, bokhyllor, byråer, linneskåp och hyllsystem.",
            },
            {
              label: "Fåtöljer & övrigt",
              text: "Öronlappsfåtöljer, kontorsstolar, kassaskåp, träningsutrustning och allt annat som är tungt eller skrymmande.",
            },
            {
              label: "Utemöbler",
              text: "Trädgårdsgrupper, parasoll, loungemöbler och grillar.",
            },
          ],
          afterContent:
            "Kort sagt: om det är en möbel så kan vi flytta den. Beskriv vad som ska flyttas vid bokning så anpassar vi fordon och team.",
        },
        {
          heading: "Varför boka möbelflytt med Ungdomskraft?",
          content:
            "Ungdomskraft är inte en vanlig flyttfirma. Vi är ett socialt företag som sysselsätter ungdomar och ger dem riktig arbetslivserfarenhet – och det märks i varje möbelflytt vi genomför. När du bokar möbelflytt hos oss får du ett team av motiverade, energiska unga människor som verkligen vill leverera ett felfritt jobb. Varje uppdrag är för dem en chans att visa vad de går för, och den inställningen genomsyrar allt – från hur de skyddar din soffa med möbelfiltar till hur de manövrerar en garderob genom ett trångt trapphus.\n\nSedan starten 2014 har vi flyttat tusentals möbler i Stockholms- och Göteborgsområdet. Under den resan har vi gett tusentals ungdomar deras första riktiga arbetslivserfarenhet – med allt vad det innebär: att komma i tid, kommunicera professionellt med kunder, lösa problem på plats, samarbeta i team och ta ansvar för resultatet. Våra medarbetare lär sig lyftteknik, säker lastning och hur man hanterar allt från ömtåligt antikt trä till 200-kilosoffor. De tränas i att skydda kundens egendom, golv, väggar och dörrkarmar – och i att alltid lämna både hämtadress och leveransadress i gott skick.\n\nDet här är inte bara bra för ungdomarna – det är bra för dig som kund. Du märker skillnaden i engagemang och energi. Våra team springer där andra går, dubbelkollar att alla möbler är rätt skyddade och frågar alltid om det finns något mer de kan hjälpa till med. Vi har byggt en kultur där varje möbelflytt ses som en möjlighet att överträffa förväntningarna, och det syns i de hundratals femstjärniga recensioner vi fått på Trustpilot och Google.\n\nMen det handlar inte bara om kvalitet – det handlar om mening. Varje möbelflytt du bokar genom Ungdomskraft bidrar direkt till att en ung person får arbetslivserfarenhet, lär sig ett yrke och bygger sitt cv. Många av våra medarbetare går vidare till fasta anställningar inom logistik, transport eller serviceyrken – och de bär med sig de värderingar och den arbetsmoral de lärt sig hos oss. Att välja oss innebär att din möbelflytt blir en del av något större.\n\nVi tar också hållbarhet på allvar. Möbler som inte längre behövs sorterar vi och kör till rätt återvinningsstation eller skänker till välgörenhetsorganisationer som Myrorna, Erikshjälpen och Stadsmissionen. Ingenting slängs i onödan.\n\nDet är professionell möbelflytt med mening.\n\nMöbelflytt till eller från din bostad ger rätt till RUT-avdrag. Du får avdrag på 50 % av arbetskostnaden – upp till 75 000 kr per person och år. Avdraget hanteras direkt på fakturan, så du betalar bara halva priset för arbetet.",
        },
      ],
    },

    faq: [
      {
        question: "Hur mycket kostar det att flytta möbler?",
        answer:
          "Priset beror på antal möbler, avstånd och om demontering behövs. En enskild soffa eller säng landar ofta på 595–995 kr. En hel möbelgrupp med flera tunga delar kan landa på 1 495–2 495 kr. Du ser priset direkt vid bokning.",
      },
      {
        question: "Kan jag få RUT-avdrag på möbelflytt?",
        answer:
          "Ja, möbelflytt ger rätt till RUT-avdrag när möblerna flyttas till eller från din bostad. Du får avdrag på 50 % av arbetskostnaden, upp till 75 000 kr per person och år. Avdraget gäller demontering, bärning, transport och montering. Vi hanterar RUT-avdraget direkt på fakturan, så du betalar bara halva arbetskostnaden.",
      },
      {
        question: "Kan ni flytta bara en möbel?",
        answer:
          "Ja, du behöver inte ha en hel flytt. Vi flyttar enskilda möbler – en soffa, en garderob, ett matbord eller vad som helst. Beskriv vad som ska flyttas och varifrån, så ser du priset direkt.",
      },
      {
        question: "Ingår skåpbil när ni flyttar möbler?",
        answer:
          "Ja, skåpbil ingår alltid. Vi väljer rätt storlek utifrån vad som ska flyttas – du behöver inte hyra bil, köra eller tanka.",
      },
      {
        question: "Kan ni flytta möbler jag köpt på Blocket?",
        answer:
          "Ja, vi hämtar möbler på säljarens adress, bär ut, lastar skåpbilen och kör till din adress. Vi bär in och ställer möblerna där du vill ha dem. Perfekt för Blocket, Facebook Marketplace, Tradera och auktionsköp.",
      },
      {
        question: "Demonterar och monterar ni möbler?",
        answer:
          "Ja, vi demonterar garderober, sängramar, bord och hyllor som behöver tas isär för transport. På den nya adressen monterar vi ihop allt igen. Spara gärna skruvar och beslag i märkta påsar.",
      },
      {
        question: "Skyddar ni möblerna under transporten?",
        answer:
          "Ja, vi har möbelfiltar, bärremmar, skyddsmattor och spännband. Vi skyddar både möblerna och fastigheterna vid ut- och inbärning. Allt är försäkrat – om något skadas ersätter vi det.",
      },
      {
        question: "Kan ni flytta möbler uppför trappor utan hiss?",
        answer:
          "Ja, vi är vana vid trappbärning i hisslösa hus. Oavsett om det gäller en soffa till fjärde våningen eller en garderob ner från tredje våningen har vi erfarenhet och rätt teknik. Beskriv antal trappor vid bokning.",
      },
      {
        question: "Kan ni flytta möbler vid renovering?",
        answer:
          "Ja, vi flyttar möbler till ett annat rum, förråd eller tillfällig adress under renoveringen. När den är klar kan vi flytta tillbaka allt. Perfekt vid golvslipning, målning eller köksrenovering.",
      },
      {
        question: "Kan ni flytta tunga möbler som kassaskåp eller stora vitvaror?",
        answer:
          "Ja, vi flyttar kassaskåp, tunga vitvaror och andra extra tunga föremål. Vi har rätt utrustning och teknik för att bära och transportera tungt gods säkert. Ange vikt och mått vid bokning så vi kan planera rätt.",
      },
      {
        question: "Hur snabbt kan ni flytta mina möbler?",
        answer:
          "Vi erbjuder ofta möbelflytt med kort varsel – ibland redan samma vecka. Boka online och se tillgängliga tider direkt.",
      },
      {
        question: "Kan ni flytta möbler på helger och kvällar?",
        answer:
          "Ja, vi erbjuder möbelflytt även på helger och vissa kvällar. Boka online och se alla tillgängliga tider.",
      },
      {
        question: "Flyttar ni möbler inom samma bostad?",
        answer:
          "Ja, vi flyttar möbler mellan rum, våningar eller från lägenhet till källarförråd. Perfekt när du vill möblera om utan att slita ryggen.",
      },
      {
        question: "Kan ni flytta möbler mellan Stockholm och Göteborg?",
        answer:
          "Ja, vi flyttar möbler både inom och mellan Stockholm och Göteborg. Priset beräknas utifrån avstånd och mängd. Ange adresserna vid bokning så ser du kostnaden direkt.",
      },
      {
        question: "I vilka kommuner erbjuder ni möbelflytt?",
        answer:
          "Vi erbjuder möbelflytt i hela Stockholmsområdet: Botkyrka, Danderyd, Ekerö, Haninge, Huddinge, Järfälla, Lidingö, Nacka, Norrtälje, Nykvarn, Nynäshamn, Salem, Sigtuna, Sollentuna, Solna, Stockholm, Sundbyberg, Södertälje, Tyresö, Täby, Upplands Väsby, Upplands-Bro, Vallentuna, Vaxholm, Värmdö och Österåker. Vi finns även i Göteborgsområdet: Ale, Alingsås, Göteborg, Härryda, Kungälv, Kungsbacka, Lerum, Lilla Edet, Mölndal, Partille, Stenungsund, Tjörn och Öckerö.",
      },
    ],

    priceHint: PACKAGE_PRICE_HINT_SV.transport,

    pricingRows: getPackagePricingRows("transport", "sv"),

    dynamicHeadings: {
      threeSteps: "Flytta möbler i 3 enkla steg",
      step2Text:
        "Våra ungdomar skyddar möblerna, bär ut dem genom trappor och trånga dörrar, lastar skåpbilen och kör till den nya adressen. Sedan bär vi in och ställer allt på rätt plats.",
      step3Title: "Möblerna på plats",
      step3Desc:
        "Vi bär in dina möbler och ställer dem exakt där du vill ha dem. Möbler som demonterats monteras ihop igen. Klart.",
      ctaHeading: "Boka möbelflytt idag",
      ctaText:
        "Ange hämtadress och leveransadress, beskriv vilka möbler som ska flyttas och välj en tid. Du ser priset direkt.",
    },

    en: {
      slug: "furniture-moving",
      meta: {
        title: "Furniture Moving – Pickup, Transport & Delivery | Ungdomskraft",
        description:
          "Book furniture moving with van and crew. We pick up, carry, drive and place your furniture at the new address. Perfect for purchases, renovations or rearranging. Fixed price.",
        h1: "Furniture moving",
      },
      hero: {
        heading: "Help with furniture moving",
        intro:
          "Need to move a sofa, dining table or a full set of furniture to a new address? We pick up the furniture where it stands, carry it out, drive it by van and carry it into the new location. Perfect for second-hand purchases, rearranging, renovations or furnishing a new home. No need to rent a car, carry it yourself or call around for help.",
      },
      featureCards: {
        sinceText: "Furniture moving since 2014",
        bookYourText: "Book your furniture move",
        bookDescText:
          "Enter the pickup and delivery address, describe which furniture needs moving and pick a time. You see the price instantly.",
        vehicleTitleText: "Van sized for your furniture",
        vehicleDescText:
          "We arrive with the right vehicle for your furniture. A single armchair or a complete dining set — we match the van to the job and make sure everything is transported safely.",
      },
      itemsSection: {
        heading: "Common furniture we move",
        items: [
          {
            name: "Sofas & corner sofas",
            description: "Sofa beds, corner sofas and heavy leather sofas — protected and carried",
            image: "/sofa.webp",
          },
          {
            name: "Beds & mattresses",
            description: "Double beds, continental beds and sprung mattresses",
            image: "/bed.webp",
          },
          {
            name: "Dining tables & chairs",
            description: "Dining sets, coffee tables, desks and chairs",
            image: "/table.webp",
          },
          {
            name: "Wardrobes & dressers",
            description: "PAX wardrobes, linen cupboards, dressers and bookcases — disassembled if needed",
            image: "/wardrobe.webp",
          },
          {
            name: "Armchairs & seating",
            description: "Wing chairs, office chairs and lounge chairs",
            image: "/armchair.webp",
          },
          {
            name: "Garden furniture",
            description: "Outdoor dining sets, parasols and lounge furniture",
            image: "/patio-furniture.webp",
          },
        ],
      },
      seoContent: {
        mainHeading: "Furniture moving — pickup, transport and delivery in one package",
        mainText:
          "Sometimes you don't need to move an entire home — just a sofa going to the new flat, a dining table you bought second-hand or a wardrobe coming from your parents' place. With Ungdomskraft's furniture moving service you get a team that picks up the furniture where it stands, carries it out through stairs and narrow doorways, loads the van and drives to the new address. There we carry everything in and place the furniture exactly where you want it. We disassemble anything that needs taking apart and reassemble it on-site. Everything is included in one booking: vehicle, crew, protective materials and insurance. You don't need to rent a car, drive yourself or carry anything heavy. Whether it's a single piece of furniture or a complete set, we scale the team and vehicle to match the job. Many customers book us for second-hand purchases, renovations, rearranging or furnishing a new home.",
        preparationHeading: "Preparing for your furniture move",
        preparationText:
          "Measure your furniture and check that it fits through doorways and stairwells at both addresses. Large items like wardrobes, bed frames and dining tables are often quicker to move in pieces — disassemble them beforehand if you can, or let us do it. Collect screws and fittings in labelled bags. Empty drawers and shelves before we arrive — it makes the furniture lighter and reduces the risk of items falling out. Make sure there is clear access and that the van can park close to the entrance at both addresses.",
        additionalSections: [
          {
            heading: "Furniture moving for purchases and sales",
            content:
              "Bought a sofa on a marketplace, Facebook or at auction? Or sold furniture and need to deliver it to the buyer? We pick up the furniture at the seller's address, carry it out, load, drive and carry it in at the buyer's. You don't need to rent a car, ask for help or risk damaging the furniture or your vehicle. The price is fixed and you see it when booking — whether it's a single armchair or a full dining set.",
          },
          {
            heading: "Furniture moving during renovation and rearranging",
            content:
              "Renovating and need to move furniture to another room, a storage unit or a temporary address? We carry the furniture out, drive it where it needs to go and carry it in again. When the renovation is finished, we can move everything back. We also handle furniture that needs to change position within the same home — between floors, rooms or from flat to basement storage. Perfect for floor sanding, painting or kitchen renovations.",
          },
          {
            heading: "What furniture can you move?",
            listItems: [
              {
                label: "Sofas",
                text: "Sofa beds, corner sofas, leather sofas and sofa sets — protected and carried.",
              },
              {
                label: "Beds",
                text: "Single beds, double beds, continental beds and bed frames — disassembled if needed.",
              },
              {
                label: "Tables & chairs",
                text: "Dining tables, dining sets, desks, coffee tables and chairs of all kinds.",
              },
              {
                label: "Storage",
                text: "Wardrobes, bookcases, dressers, linen cupboards and shelving systems.",
              },
              {
                label: "Armchairs & other",
                text: "Wing chairs, office chairs, safes, gym equipment and anything else that is heavy or bulky.",
              },
              {
                label: "Outdoor furniture",
                text: "Garden dining sets, parasols, lounge furniture and barbecues.",
              },
            ],
            afterContent:
              "In short: if it's furniture, we can move it. Describe what needs moving when booking and we'll match the vehicle and team.",
          },
          {
            heading: "Why book furniture moving with Ungdomskraft?",
            content:
              "Ungdomskraft is not an ordinary removal company. We are a social enterprise that employs young people and gives them real work experience — and it shows in every furniture move we carry out. When you book furniture moving with us, you get a team of motivated, energetic young workers who genuinely want to deliver a flawless job. Every assignment is a chance for them to prove themselves, and that mindset runs through everything — from how they wrap your sofa in furniture blankets to how they manoeuvre a wardrobe through a tight stairwell.\n\nSince our founding in 2014, we have moved thousands of pieces of furniture across the Stockholm and Gothenburg regions. Along the way, we have given thousands of young people their first real work experience — with everything that entails: turning up on time, communicating professionally with customers, solving problems on-site, collaborating as a team and taking responsibility for the result. Our workers learn lifting techniques, safe loading and how to handle everything from delicate antique wood to 200-kilo sofas. They are trained to protect customers' property, floors, walls and door frames — and to always leave both pickup and delivery addresses in good condition.\n\nThis is not just good for the young people — it is good for you as a customer. You notice the difference in commitment and energy. Our teams run where others walk, double-check that all furniture is properly protected and always ask if there is anything else they can help with. We have built a culture where every furniture move is seen as an opportunity to exceed expectations, and it shows in the hundreds of five-star reviews we have received on Trustpilot and Google.\n\nBut it is not just about quality — it is about meaning. Every furniture move you book through Ungdomskraft directly contributes to a young person gaining work experience, learning a trade and building their CV. Many of our workers go on to permanent positions in logistics, transport or service industries — carrying with them the values and work ethic they learned with us. Choosing us means your furniture move becomes part of something bigger.\n\nWe also take sustainability seriously. Furniture that is no longer needed is sorted and taken to the right recycling station or donated to charities such as Myrorna, Erikshjälpen and Stadsmissionen. Nothing is dumped unnecessarily.\n\nIt is professional furniture moving with purpose.\n\nFurniture moving to or from your home qualifies for the Swedish RUT tax deduction. You get a 50% deduction on the labour cost – up to SEK 75,000 per person per year. The deduction is handled directly on the invoice, so you only pay half the price for the work.",
          },
        ],
      },
      faq: [
        {
          question: "How much does it cost to move furniture?",
          answer:
            "The price depends on the number of pieces, distance and whether disassembly is needed. A single sofa or bed often comes to SEK 595–995. A full furniture set with several heavy pieces can come to SEK 1,495–2,495. You see the price instantly when booking.",
        },
        {
          question: "Can I get a RUT tax deduction on furniture moving?",
          answer:
            "Yes, furniture moving qualifies for the Swedish RUT tax deduction when the furniture is moved to or from your home. You get a 50% deduction on the labour cost, up to SEK 75,000 per person per year. The deduction covers disassembly, carrying, transport and assembly. We handle the RUT deduction directly on the invoice, so you only pay half the labour cost.",
        },
        {
          question: "Can you move just one piece of furniture?",
          answer:
            "Yes, you don't need a full house move. We move individual pieces — a sofa, a wardrobe, a dining table or anything else. Describe what needs moving and where, and you see the price instantly.",
        },
        {
          question: "Is a van included when you move furniture?",
          answer:
            "Yes, a van is always included. We choose the right size based on what needs moving — you don't need to rent a car, drive or refuel.",
        },
        {
          question: "Can you move furniture I bought second-hand?",
          answer:
            "Yes, we pick up furniture at the seller's address, carry it out, load the van and drive to your address. We carry it in and place it where you want. Perfect for marketplace purchases, Facebook, auction buys and more.",
        },
        {
          question: "Do you disassemble and reassemble furniture?",
          answer:
            "Yes, we disassemble wardrobes, bed frames, tables and shelves that need taking apart for transport. At the new address we reassemble everything. Keep screws and fittings in labelled bags.",
        },
        {
          question: "Do you protect furniture during transport?",
          answer:
            "Yes, we bring furniture blankets, carrying straps, floor mats and ratchet straps. We protect both the furniture and the property when carrying in and out. Everything is insured — if anything gets damaged, we compensate for it.",
        },
        {
          question: "Can you carry furniture up stairs with no lift?",
          answer:
            "Yes, we are experienced with stair carrying in buildings with no lift. Whether it's a sofa to the fourth floor or a wardrobe down from the third, we have the experience and technique. Mention the number of floors when booking.",
        },
        {
          question: "Can you move furniture during a renovation?",
          answer:
            "Yes, we move furniture to another room, storage unit or temporary address during renovation. When it's done, we can move everything back. Perfect for floor sanding, painting or kitchen renovations.",
        },
        {
          question: "Can you move heavy items like safes and large appliances?",
          answer:
            "Yes, we move safes, heavy appliances and other extra-heavy items. We have the right equipment and technique to carry and transport heavy goods safely. Enter the weight and dimensions when booking so we can plan correctly.",
        },
        {
          question: "How quickly can you move my furniture?",
          answer:
            "We often offer furniture moving at short notice — sometimes the same week. Book online and you'll see available times straight away.",
        },
        {
          question: "Can you move furniture on weekends and evenings?",
          answer:
            "Yes, we offer furniture moving on weekends and some evenings. Book online and you'll see all available slots.",
        },
        {
          question: "Do you move furniture within the same home?",
          answer:
            "Yes, we move furniture between rooms, floors or from flat to basement storage. Perfect when you want to rearrange without straining your back.",
        },
        {
          question: "Can you move furniture between Stockholm and Gothenburg?",
          answer:
            "Yes, we move furniture both within and between Stockholm and Gothenburg. The price is based on distance and volume. Enter both addresses when booking and you see the cost instantly.",
        },
        {
          question: "Which areas do you offer furniture moving in?",
          answer:
            "We offer furniture moving throughout the Stockholm region: Botkyrka, Danderyd, Ekerö, Haninge, Huddinge, Järfälla, Lidingö, Nacka, Norrtälje, Nykvarn, Nynäshamn, Salem, Sigtuna, Sollentuna, Solna, Stockholm, Sundbyberg, Södertälje, Tyresö, Täby, Upplands Väsby, Upplands-Bro, Vallentuna, Vaxholm, Värmdö and Österåker. We also operate in the Gothenburg area: Ale, Alingsås, Göteborg, Härryda, Kungälv, Kungsbacka, Lerum, Lilla Edet, Mölndal, Partille, Stenungsund, Tjörn and Öckerö.",
        },
      ],
      priceHint: PACKAGE_PRICE_HINT_EN.transport,
      pricingRows: getPackagePricingRows("transport", "en"),
      dynamicHeadings: {
        threeSteps: "Furniture moving in 3 easy steps",
        step2Text:
          "Our workers protect the furniture, carry it out through stairs and narrow doorways, load the van and drive to the new address. Then we carry everything in and place it where you want it.",
        step3Title: "Furniture in place",
        step3Desc:
          "We carry your furniture in and place it exactly where you want it. Anything we disassembled is reassembled. Done.",
        ctaHeading: "Book furniture moving today",
        ctaText:
          "Enter the pickup and delivery address, describe which furniture needs moving and pick a time. You see the price instantly.",
      },
    },
  },
];
